import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const SECRET_KEY = 'adminSecret';
const TAB_KEY = 'adminActiveTab';
const VALID_TABS = ['tos', 'privacy', 'beta', 'appSettings', 'newsletter', 'jcal'];

const DOCUMENT_TYPES = {
    tos: {
        path: '/admin/tos',
        title: 'Terms of Service',
        shortLabel: 'TOS',
        entryLabel: 'TOS entry',
        entriesLabel: 'TOS entries',
    },
    privacy: {
        path: '/admin/privacy',
        title: 'Privacy Policy',
        shortLabel: 'Privacy',
        entryLabel: 'privacy policy entry',
        entriesLabel: 'privacy policy entries',
    },
};

const ALL_GROUPS = [
    'ios-beta-testers@happtrak.ai',
    'ios-beta-waitlist@happtrak.ai',
    'android-beta-testers@happtrak.ai',
    'android-beta-waitlist@happtrak.ai',
];

const emptyDocForm = {
    app: 'happtrak',
    version: '',
    language: 'en',
    jurisdiction: 'global',
    content: '',
    effective_date: '',
};

const emptyBetaConfig = {
    cap: 20,
    ios_testflight_link: '',
    android_play_store_link: '',
    ios_signups_enabled: true,
    android_signups_enabled: true,
};

const emptyAppSettings = {
    event_feedback_prompt_probability: 0.3,
    interstitial_ad_probability: 0.0,
    ad_gate_mode: 'random',
    ad_gate_rollout_pct: 100,
    ad_gate_active_days: 5,
};

const emptyJcalSettings = {
    event_feedback_prompt_probability: 0.3,
    interstitial_ad_probability: 0.1,
    ad_gate_mode: 'random',
    ad_gate_rollout_pct: 100,
    ad_gate_active_days: 5,
    voice_session_budget_usd: 0.25,
    voice_session_max_openai_tokens: 4096,
    voice_daily_session_limit: 5,
    friend_suggestion_threshold: 2,
};

const emptySignupsData = {
    signups: [],
    ios_testers: 0,
    ios_waitlist: 0,
    android_testers: 0,
    android_waitlist: 0,
    registered_count: 0,
    open_instances: 0,
};

const normalizeBetaConfig = (data) => ({
    ...emptyBetaConfig,
    ...(data && typeof data === 'object' ? data : {}),
});

const toCount = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeSignupsData = (data) => ({
    ...emptySignupsData,
    signups: Array.isArray(data?.signups) ? data.signups : [],
    ios_testers: toCount(data?.ios_testers),
    ios_waitlist: toCount(data?.ios_waitlist),
    android_testers: toCount(data?.android_testers),
    android_waitlist: toCount(data?.android_waitlist),
    registered_count: toCount(data?.registered_count),
    open_instances: toCount(data?.open_instances),
});

function AdminPanel() {
    const [activeTab, setActiveTab] = useState(() => {
        const saved = localStorage.getItem(TAB_KEY);
        return saved && VALID_TABS.includes(saved) ? saved : 'tos';
    });
    const [secret, setSecret] = useState(sessionStorage.getItem(SECRET_KEY) || '');
    const [authed, setAuthed] = useState(false);
    const [secretInput, setSecretInput] = useState('');
    const [authError, setAuthError] = useState('');

    // Document tab state (TOS / Privacy)
    const [documentType, setDocumentType] = useState('tos');
    const [entries, setEntries] = useState([]);
    const [loadError, setLoadError] = useState('');
    const [form, setForm] = useState(emptyDocForm);
    const [editingId, setEditingId] = useState(null);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [saving, setSaving] = useState(false);

    // App Settings tab state
    const [appSettings, setAppSettings] = useState(emptyAppSettings);
    const [appSettingsError, setAppSettingsError] = useState('');
    const [appSettingsSuccess, setAppSettingsSuccess] = useState('');
    const [appSettingsSaving, setAppSettingsSaving] = useState(false);

    // Beta tab state
    const [betaConfig, setBetaConfig] = useState(emptyBetaConfig);
    const [betaConfigError, setBetaConfigError] = useState('');
    const [betaConfigSuccess, setBetaConfigSuccess] = useState('');
    const [selectedSignupIds, setSelectedSignupIds] = useState(new Set());
    const [deleting, setDeleting] = useState(false);
    const [betaConfigSaving, setBetaConfigSaving] = useState(false);
    const [signupsData, setSignupsData] = useState(null);
    const [signupsError, setSignupsError] = useState('');
    const [promotingAll, setPromotingAll] = useState(false);
    const [bulkPromoting, setBulkPromoting] = useState(false);
    const [reassigning, setReassigning] = useState({});

    // Newsletter tab state
    const [newsletterEmail, setNewsletterEmail] = useState('');
    const [newsletterLoading, setNewsletterLoading] = useState(false);
    const [newsletterError, setNewsletterError] = useState('');
    const [newsletterResult, setNewsletterResult] = useState(null);
    const [newsletterMessagesLoading, setNewsletterMessagesLoading] = useState(false);
    const [newsletterMessagesError, setNewsletterMessagesError] = useState('');
    const [newsletterMessages, setNewsletterMessages] = useState({ messages: [] });

    // JCAL tab state
    const [jcalSettings, setJcalSettings] = useState(emptyJcalSettings);
    const [jcalSettingsError, setJcalSettingsError] = useState('');
    const [jcalSettingsSuccess, setJcalSettingsSuccess] = useState('');
    const [jcalSettingsSaving, setJcalSettingsSaving] = useState(false);
    const [jcalApp, setJcalApp] = useState(null);
    const [jcalError, setJcalError] = useState('');
    const [jcalSuccess, setJcalSuccess] = useState('');
    const [jcalLoading, setJcalLoading] = useState(false);
    const [jcalGroups, setJcalGroups] = useState({ groups: [], total: 0, page: 1, page_size: 50 });
    const [jcalNewsletters, setJcalNewsletters] = useState([]);
    const [jcalUsers, setJcalUsers] = useState([]);
    const [jcalSearch, setJcalSearch] = useState('');
    const [jcalUserId, setJcalUserId] = useState('');
    const [jcalPage, setJcalPage] = useState(1);
    const [jcalSaving, setJcalSaving] = useState({});
    const [jcalSortBy, setJcalSortBy] = useState('');
    const [jcalSortDir, setJcalSortDir] = useState('asc');
    const [jcalSyncPanel, setJcalSyncPanel] = useState(null); // { group, collectionKey }
    const [jcalSyncStats, setJcalSyncStats] = useState({}); // `${group_id}::${key}` → { loading, available }
    const [jcalSyncSincedays, setJcalSyncSinceDays] = useState({}); // `${group_id}::${key}` → number
    const [jcalLaunching, setJcalLaunching] = useState({}); // `${group_id}::${key}` → bool
    const [jcalPurging, setJcalPurging] = useState({}); // `${group_id}::${key}` → bool
    const [jcalActiveJobs, setJcalActiveJobs] = useState({}); // `${group_id}::${key}` → job object

    const headers = useMemo(() => ({ 'X-Admin-Secret': secret }), [secret]);
    const docConfig = DOCUMENT_TYPES[documentType];
    const docEndpoint = `${API_URL}${docConfig.path}`;
    const safeBetaConfig = normalizeBetaConfig(betaConfig);
    const safeSignupsData = signupsData ? normalizeSignupsData(signupsData) : null;
    const signups = safeSignupsData?.signups || [];

    const fetchEntries = useCallback(async (s, type = documentType) => {
        const activeConfig = DOCUMENT_TYPES[type];
        setLoadError('');
        try {
            const res = await axios.get(`${API_URL}${activeConfig.path}`, { headers: { 'X-Admin-Secret': s } });
            setEntries(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            if (err.response?.status === 401) {
                setAuthed(false);
                setAuthError('Invalid secret.');
                sessionStorage.removeItem(SECRET_KEY);
                return false;
            } else {
                setLoadError(`Failed to load ${activeConfig.entriesLabel}.`);
            }
        }
    }, [documentType]);

    const fetchBetaConfig = useCallback(async (s) => {
        try {
            const res = await axios.get(`${API_URL}/admin/beta`, { headers: { 'X-Admin-Secret': s } });
            setBetaConfig(normalizeBetaConfig(res.data));
        } catch (err) {
            setBetaConfigError('Failed to load beta config.');
        }
    }, []);

    const fetchSignups = useCallback(async (s) => {
        setSignupsError('');
        try {
            const res = await axios.get(`${API_URL}/admin/beta/signups`, { headers: { 'X-Admin-Secret': s } });
            setSignupsData(normalizeSignupsData(res.data));
        } catch (err) {
            setSignupsError('Failed to load signups.');
        }
    }, []);

    const fetchAppSettings = useCallback(async (s) => {
        try {
            const res = await axios.get(`${API_URL}/admin/app-settings/happtrak`, { headers: { 'X-Admin-Secret': s } });
            setAppSettings(res.data);
        } catch (err) {
            setAppSettingsError('Failed to load app settings.');
        }
    }, []);

    const fetchJcalSettings = useCallback(async (s) => {
        try {
            const res = await axios.get(`${API_URL}/admin/app-settings/jcal`, { headers: { 'X-Admin-Secret': s } });
            setJcalSettings(res.data);
        } catch (err) {
            setJcalSettingsError('Failed to load JCAL settings.');
        }
    }, []);

    const fetchJcalApp = useCallback(async (s, page = 1, search = '', userId = '', sortBy = '', sortDir = 'asc') => {
        setJcalError('');
        setJcalLoading(true);
        try {
            const [appRes, groupsRes, usersRes, newslettersRes] = await Promise.all([
                axios.get(`${API_URL}/admin/apps/jcal`, { headers: { 'X-Admin-Secret': s }, timeout: 15000 }),
                axios.get(`${API_URL}/admin/apps/jcal/groups`, {
                    headers: { 'X-Admin-Secret': s },
                    params: { page, page_size: 50, search, user_id: userId || undefined, sort_by: sortBy || undefined, sort_dir: sortDir },
                    timeout: 15000,
                }),
                axios.get(`${API_URL}/admin/apps/jcal/users`, { headers: { 'X-Admin-Secret': s }, timeout: 15000 }),
                axios.get(`${API_URL}/admin/apps/jcal/newsletter-groups`, { headers: { 'X-Admin-Secret': s }, timeout: 15000 }),
            ]);
            setJcalApp(appRes.data);
            setJcalGroups(groupsRes.data);
            setJcalUsers(usersRes.data);
            setJcalNewsletters(newslettersRes.data.newsletters || []);
        } catch (err) {
            setJcalError(
                err.code === 'ECONNABORTED'
                    ? 'Timed out loading JCAL config.'
                    : 'Failed to load JCAL config.'
            );
        } finally {
            setJcalLoading(false);
        }
    }, []);

    const fetchNewsletterMessages = useCallback(async (s) => {
        setNewsletterMessagesError('');
        setNewsletterMessagesLoading(true);
        try {
            const res = await axios.get(`${API_URL}/admin/newsletter/messages`, {
                headers: { 'X-Admin-Secret': s },
                params: { limit: 50 },
                timeout: 15000,
            });
            setNewsletterMessages({
                ...res.data,
                messages: Array.isArray(res.data?.messages) ? res.data.messages : [],
            });
        } catch (err) {
            setNewsletterMessagesError(
                err.code === 'ECONNABORTED'
                    ? 'Timed out loading newsletter messages.'
                    : 'Failed to load newsletter messages.'
            );
        } finally {
            setNewsletterMessagesLoading(false);
        }
    }, []);

    const handleSecretSubmit = async (e) => {
        e.preventDefault();
        setAuthError('');
        try {
            await axios.get(`${API_URL}/admin/tos`, { headers: { 'X-Admin-Secret': secretInput } });
            sessionStorage.setItem(SECRET_KEY, secretInput);
            setSecret(secretInput);
            setAuthed(true);
            fetchEntries(secretInput, documentType);
        } catch (err) {
            if (err.response?.status === 401) {
                setAuthError('Invalid secret.');
            } else {
                setAuthError('Could not connect to API.');
            }
        }
    };

    useEffect(() => {
        if (secret) {
            fetchEntries(secret, documentType).then((ok) => { if (ok !== false) setAuthed(true); });
        }
    }, [secret, documentType, fetchEntries]);

    useEffect(() => {
        if (authed && secret && activeTab === 'beta') {
            fetchBetaConfig(secret);
            fetchSignups(secret);
        }
        if (authed && secret && activeTab === 'appSettings') {
            fetchAppSettings(secret);
        }
        if (authed && secret && activeTab === 'jcal') {
            fetchJcalApp(secret);
            fetchJcalSettings(secret);
        }
        if (authed && secret && activeTab === 'newsletter') {
            fetchNewsletterMessages(secret);
        }
    }, [authed, secret, activeTab, fetchBetaConfig, fetchSignups, fetchAppSettings, fetchJcalApp, fetchJcalSettings, fetchNewsletterMessages]);

    const switchTab = (tab) => {
        setActiveTab(tab);
        localStorage.setItem(TAB_KEY, tab);
        setFormError('');
        setFormSuccess('');
        setAppSettingsError('');
        setAppSettingsSuccess('');
        setNewsletterError('');
        setNewsletterMessagesError('');
        setJcalError('');
        setJcalSuccess('');
        if (tab === 'jcal' && secret) fetchJcalApp(secret);
        if (tab === 'newsletter' && secret) fetchNewsletterMessages(secret);
        if (tab === 'tos' || tab === 'privacy') {
            const type = tab;
            setDocumentType(type);
            setEditingId(null);
            setForm(emptyDocForm);
            if (secret) fetchEntries(secret, type);
        }
    };

    const handleLogout = () => {
        sessionStorage.removeItem(SECRET_KEY);
        setSecret('');
        setAuthed(false);
        setSecretInput('');
        setEntries([]);
        setSignupsData(null);
        setNewsletterResult(null);
        setNewsletterMessages({ messages: [] });
    };

    const handleBulkDelete = async () => {
        if (selectedSignupIds.size === 0) return;
        if (!window.confirm(`Delete ${selectedSignupIds.size} signup(s)? This also removes them from their Google Group.`)) return;
        setDeleting(true);
        try {
            await axios.delete(`${API_URL}/admin/beta/signups`, {
                headers,
                data: { ids: Array.from(selectedSignupIds) },
            });
            setSelectedSignupIds(new Set());
            fetchSignups(secret);
        } catch (err) {
            alert(err.response?.data?.detail || 'Delete failed.');
        } finally {
            setDeleting(false);
        }
    };

    // Document form handlers
    const startEdit = (entry) => {
        setEditingId(entry.id);
        setForm({
            app: entry.app || 'happtrak',
            version: entry.version,
            language: entry.language,
            jurisdiction: entry.jurisdiction,
            content: entry.content,
            effective_date: entry.effective_date ? entry.effective_date.slice(0, 16) : '',
        });
        setFormError('');
        setFormSuccess('');
    };

    const startCreate = () => {
        setEditingId(null);
        setForm(emptyDocForm);
        setFormError('');
        setFormSuccess('');
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setFormError('');
        setFormSuccess('');
        try {
            if (editingId) {
                await axios.put(`${docEndpoint}/${editingId}`, {
                    content: form.content,
                    effective_date: form.effective_date ? new Date(form.effective_date).toISOString() : undefined,
                }, { headers });
            } else {
                await axios.post(docEndpoint, {
                    ...form,
                    version: parseInt(form.version, 10),
                    effective_date: new Date(form.effective_date).toISOString(),
                }, { headers });
            }
            setFormSuccess(editingId ? 'Updated.' : 'Created.');
            setEditingId(null);
            setForm(emptyDocForm);
            fetchEntries(secret, documentType);
        } catch (err) {
            setFormError(err.response?.data?.detail || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(`Delete this ${docConfig.entryLabel}?`)) return;
        try {
            await axios.delete(`${docEndpoint}/${id}`, { headers });
            fetchEntries(secret, documentType);
        } catch (err) {
            alert(err.response?.data?.detail || 'Delete failed.');
        }
    };

    // Beta config handlers
    const handleBetaConfigSave = async (e) => {
        e.preventDefault();
        setBetaConfigSaving(true);
        setBetaConfigError('');
        setBetaConfigSuccess('');
        try {
            const res = await axios.put(`${API_URL}/admin/beta`, {
                cap: parseInt(safeBetaConfig.cap, 10),
                ios_testflight_link: safeBetaConfig.ios_testflight_link,
                android_play_store_link: safeBetaConfig.android_play_store_link,
                ios_signups_enabled: safeBetaConfig.ios_signups_enabled,
                android_signups_enabled: safeBetaConfig.android_signups_enabled,
            }, { headers });
            setBetaConfig(normalizeBetaConfig(res.data));
            setBetaConfigSuccess('Saved.');
        } catch (err) {
            setBetaConfigError(err.response?.data?.detail || 'Save failed.');
        } finally {
            setBetaConfigSaving(false);
        }
    };

    const handleAppSettingsSave = async (e) => {
        e.preventDefault();
        setAppSettingsSaving(true);
        setAppSettingsError('');
        setAppSettingsSuccess('');
        try {
            const res = await axios.put(`${API_URL}/admin/app-settings/happtrak`, {
                event_feedback_prompt_probability: parseFloat(appSettings.event_feedback_prompt_probability),
                interstitial_ad_probability: parseFloat(appSettings.interstitial_ad_probability),
                ad_gate_mode: appSettings.ad_gate_mode ?? 'random',
                ad_gate_rollout_pct: parseInt(appSettings.ad_gate_rollout_pct ?? 100, 10),
                ad_gate_active_days: parseInt(appSettings.ad_gate_active_days ?? 5, 10),
            }, { headers });
            setAppSettings(res.data);
            setAppSettingsSuccess('Saved.');
        } catch (err) {
            setAppSettingsError(err.response?.data?.detail || 'Save failed.');
        } finally {
            setAppSettingsSaving(false);
        }
    };

    const handleJcalSettingsSave = async (e) => {
        e.preventDefault();
        setJcalSettingsSaving(true);
        setJcalSettingsError('');
        setJcalSettingsSuccess('');
        try {
            const res = await axios.put(`${API_URL}/admin/app-settings/jcal`, {
                event_feedback_prompt_probability: parseFloat(jcalSettings.event_feedback_prompt_probability),
                interstitial_ad_probability: parseFloat(jcalSettings.interstitial_ad_probability),
                ad_gate_mode: jcalSettings.ad_gate_mode ?? 'random',
                ad_gate_rollout_pct: parseInt(jcalSettings.ad_gate_rollout_pct ?? 100, 10),
                ad_gate_active_days: parseInt(jcalSettings.ad_gate_active_days ?? 5, 10),
                voice_session_budget_usd: parseFloat(jcalSettings.voice_session_budget_usd),
                voice_session_max_openai_tokens: parseInt(jcalSettings.voice_session_max_openai_tokens, 10),
                voice_daily_session_limit: parseInt(jcalSettings.voice_daily_session_limit, 10),
                friend_suggestion_threshold: parseInt(jcalSettings.friend_suggestion_threshold, 10),
            }, { headers });
            setJcalSettings(res.data);
            setJcalSettingsSuccess('Saved.');
        } catch (err) {
            setJcalSettingsError(err.response?.data?.detail || 'Save failed.');
        } finally {
            setJcalSettingsSaving(false);
        }
    };

    const handlePromoteAll = async () => {
        setPromotingAll(true);
        try {
            const res = await axios.post(`${API_URL}/admin/beta/signups/promote-all`, {}, { headers });
            const { promoted } = res.data;
            alert(promoted > 0 ? `Promoted ${promoted} user${promoted !== 1 ? 's' : ''}.` : 'No spots available or no one on the waitlist.');
            fetchSignups(secret);
        } catch (err) {
            alert(err.response?.data?.detail || 'Promote failed.');
        } finally {
            setPromotingAll(false);
        }
    };

    const handleBulkPromote = async () => {
        if (selectedSignupIds.size === 0) return;
        setBulkPromoting(true);
        try {
            const res = await axios.post(`${API_URL}/admin/beta/signups/bulk-promote`, { ids: Array.from(selectedSignupIds) }, { headers });
            const { promoted } = res.data;
            alert(promoted > 0 ? `Promoted ${promoted} user${promoted !== 1 ? 's' : ''}.` : 'Selected users are not on the waitlist.');
            setSelectedSignupIds(new Set());
            fetchSignups(secret);
        } catch (err) {
            alert(err.response?.data?.detail || 'Promote failed.');
        } finally {
            setBulkPromoting(false);
        }
    };

    const handleReassign = async (signupId, newGroup) => {
        setReassigning((r) => ({ ...r, [signupId]: true }));
        try {
            await axios.put(`${API_URL}/admin/beta/signups/${signupId}/group`, { group: newGroup }, { headers });
            fetchSignups(secret);
        } catch (err) {
            alert(err.response?.data?.detail || 'Reassign failed.');
        } finally {
            setReassigning((r) => ({ ...r, [signupId]: false }));
        }
    };

    const handleNewsletterCreate = async (e) => {
        e.preventDefault();
        setNewsletterLoading(true);
        setNewsletterError('');
        setNewsletterResult(null);
        try {
            const res = await axios.post(
                `${API_URL}/admin/newsletter/instances`,
                { email: newsletterEmail.trim() },
                { headers }
            );
            setNewsletterResult(res.data);
            fetchNewsletterMessages(secret);
        } catch (err) {
            setNewsletterError(err.response?.data?.detail || 'Failed to create newsletter instance.');
        } finally {
            setNewsletterLoading(false);
        }
    };

    if (!authed) {
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    <h2 style={styles.heading}>Admin Login</h2>
                    <form onSubmit={handleSecretSubmit}>
                        <input
                            type="password"
                            placeholder="Admin secret"
                            value={secretInput}
                            onChange={(e) => setSecretInput(e.target.value)}
                            style={styles.input}
                            required
                        />
                        {authError && <p style={styles.error}>{authError}</p>}
                        <button type="submit" style={styles.btn}>Login</button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <div style={styles.topBar}>
                <h1 style={styles.heading}>JCAL.app</h1>
                <button onClick={handleLogout} style={{ ...styles.btn, ...styles.btnSm }}>Logout</button>
            </div>

            <div style={styles.tabs}>
                {[
                    { key: 'tos', label: 'Terms of Service' },
                    { key: 'privacy', label: 'Privacy Policy' },
                    { key: 'beta', label: 'Beta' },
                    { key: 'appSettings', label: 'HappTrak Settings' },
                    { key: 'newsletter', label: 'Newsletter' },
                    { key: 'jcal', label: 'JCAL' },
                ].map(({ key, label }) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => switchTab(key)}
                        style={{ ...styles.tab, ...(activeTab === key ? styles.activeTab : {}) }}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {activeTab === 'jcal' ? (
                <JcalPanel
                    config={jcalApp}
                    error={jcalError}
                    success={jcalSuccess}
                    loading={jcalLoading}
                    groups={jcalGroups}
                    users={jcalUsers}
                    search={jcalSearch}
                    userId={jcalUserId}
                    page={jcalPage}
                    saving={jcalSaving}
                    headers={headers}
                    apiUrl={API_URL}
                    onRefresh={() => fetchJcalApp(secret, jcalPage, jcalSearch, jcalUserId, jcalSortBy, jcalSortDir)}
                    setError={setJcalError}
                    setSuccess={setJcalSuccess}
                    setSearch={setJcalSearch}
                    setUserId={setJcalUserId}
                    setPage={setJcalPage}
                    setSaving={setJcalSaving}
                    setGroups={setJcalGroups}
                    sortBy={jcalSortBy}
                    sortDir={jcalSortDir}
                    setSortBy={setJcalSortBy}
                    setSortDir={setJcalSortDir}
                    fetchConfig={fetchJcalApp}
                    secret={secret}
                    styles={styles}
                    jcalSettings={jcalSettings}
                    setJcalSettings={setJcalSettings}
                    jcalSettingsError={jcalSettingsError}
                    jcalSettingsSuccess={jcalSettingsSuccess}
                    jcalSettingsSaving={jcalSettingsSaving}
                    onJcalSettingsSave={handleJcalSettingsSave}
                    newsletters={jcalNewsletters}
                    setNewsletters={setJcalNewsletters}
                    syncPanel={jcalSyncPanel}
                    setSyncPanel={setJcalSyncPanel}
                    syncStats={jcalSyncStats}
                    setSyncStats={setJcalSyncStats}
                    syncSinceDays={jcalSyncSincedays}
                    setSyncSinceDays={setJcalSyncSinceDays}
                    launching={jcalLaunching}
                    setLaunching={setJcalLaunching}
                    purging={jcalPurging}
                    setPurging={setJcalPurging}
                    activeJobs={jcalActiveJobs}
                    setActiveJobs={setJcalActiveJobs}
                />
            ) : activeTab === 'appSettings' ? (
                <div style={styles.card}>
                    <h2 style={styles.subheading}>HappTrak App Settings</h2>
                    <form onSubmit={handleAppSettingsSave} style={styles.form}>
                        <div style={styles.row}>
                            <label style={styles.label}>Event Feedback Prompt Probability (0.0 – 1.0)</label>
                            <input
                                type="number"
                                value={appSettings.event_feedback_prompt_probability}
                                onChange={(e) => setAppSettings({ ...appSettings, event_feedback_prompt_probability: e.target.value })}
                                style={{ ...styles.input, width: 120 }}
                                min={0} max={1} step={0.01}
                                required
                            />
                        </div>
                        <div style={styles.row}>
                            <label style={styles.label}>Interstitial Ad Probability (0.0 – 1.0)</label>
                            <input
                                type="number"
                                value={appSettings.interstitial_ad_probability}
                                onChange={(e) => setAppSettings({ ...appSettings, interstitial_ad_probability: e.target.value })}
                                style={{ ...styles.input, width: 120 }}
                                min={0} max={1} step={0.01}
                                required
                            />
                        </div>
                        <div style={styles.row}>
                            <label style={styles.label}>Ad Mode</label>
                            <select
                                value={appSettings.ad_gate_mode ?? 'random'}
                                onChange={(e) => setAppSettings({ ...appSettings, ad_gate_mode: e.target.value })}
                                style={{ ...styles.input, width: 220 }}
                            >
                                <option value="random">Random interstitial</option>
                                <option value="gate">Gate every 1/probability opens</option>
                            </select>
                        </div>
                        <div style={styles.row}>
                            <label style={styles.label}>Gate Rollout (% of users)</label>
                            <input
                                type="number"
                                value={appSettings.ad_gate_rollout_pct ?? 100}
                                onChange={(e) => setAppSettings({ ...appSettings, ad_gate_rollout_pct: e.target.value })}
                                style={{ ...styles.input, width: 100 }}
                                min={0} max={100} step={1} required
                            />
                        </div>
                        <div style={styles.row}>
                            <label style={styles.label}>Gate Every N Active Days (0 = off)</label>
                            <input
                                type="number"
                                value={appSettings.ad_gate_active_days ?? 5}
                                onChange={(e) => setAppSettings({ ...appSettings, ad_gate_active_days: e.target.value })}
                                style={{ ...styles.input, width: 100 }}
                                min={0} max={365} step={1} required
                            />
                        </div>
                        {appSettingsError && <p style={styles.error}>{appSettingsError}</p>}
                        {appSettingsSuccess && <p style={styles.success}>{appSettingsSuccess}</p>}
                        <div>
                            <button type="submit" style={styles.btn} disabled={appSettingsSaving}>
                                {appSettingsSaving ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    </form>
                </div>
            ) : activeTab === 'newsletter' ? (
                <>
                    <div style={styles.card}>
                        <h2 style={styles.subheading}>Newsletter WhatsApp Instance</h2>
                        <form onSubmit={handleNewsletterCreate} style={styles.form}>
                            <div style={styles.row}>
                                <label htmlFor="newsletter-user-email" style={styles.label}>HappTrak User Email</label>
                                <input
                                    id="newsletter-user-email"
                                    type="email"
                                    value={newsletterEmail}
                                    onChange={(e) => setNewsletterEmail(e.target.value)}
                                    style={styles.input}
                                    placeholder="user@example.com"
                                    required
                                />
                            </div>
                            {newsletterError && <p style={styles.error}>{newsletterError}</p>}
                            <div style={styles.formActions}>
                                <button type="submit" style={styles.btn} disabled={newsletterLoading}>
                                    {newsletterLoading ? 'Creating…' : 'Create Newsletter QR'}
                                </button>
                                {newsletterResult && (
                                    <button
                                        type="button"
                                        style={{ ...styles.btn, ...styles.btnSecondary }}
                                        onClick={() => {
                                            setNewsletterEmail('');
                                            setNewsletterResult(null);
                                            setNewsletterError('');
                                        }}
                                    >
                                        Reset
                                    </button>
                                )}
                            </div>
                        </form>
                        {newsletterResult && (
                            <div style={styles.qrResult}>
                                <p style={styles.success}>Newsletter instance created.</p>
                                <p style={styles.monoLine}>User #{newsletterResult.user_id}: {newsletterResult.email}</p>
                                <p style={styles.monoLine}>Instance: {newsletterResult.instance_id}</p>
                                <img
                                    src={newsletterResult.qrcode_base64}
                                    alt={`Newsletter WhatsApp QR code for ${newsletterResult.email}`}
                                    style={styles.qrImage}
                                />
                            </div>
                        )}
                    </div>

                    <div style={styles.card}>
                        <div style={styles.tableHeader}>
                            <div>
                                <h2 style={styles.subheading}>Newsletter Messages</h2>
                                {newsletterMessages.instance_name && (
                                    <p style={{ ...styles.monoLine, marginTop: -8 }}>
                                        Instance: {newsletterMessages.instance_name}
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                style={{ ...styles.btn, ...styles.btnSm }}
                                onClick={() => fetchNewsletterMessages(secret)}
                                disabled={newsletterMessagesLoading}
                            >
                                {newsletterMessagesLoading ? 'Refreshing…' : 'Refresh'}
                            </button>
                        </div>
                        {newsletterMessagesError && <p style={styles.error}>{newsletterMessagesError}</p>}
                        {!newsletterMessagesError && !newsletterMessagesLoading && !newsletterMessages.instance_id && (
                            <p style={{ color: '#888' }}>No connected newsletter instance found.</p>
                        )}
                        {newsletterMessages.instance_id && newsletterMessages.messages.length === 0 && (
                            <p style={{ color: '#888' }}>No newsletter messages found.</p>
                        )}
                        {newsletterMessages.messages.length > 0 && (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Time</th>
                                            <th style={styles.th}>Newsletter JID</th>
                                            <th style={styles.th}>Sender</th>
                                            <th style={styles.th}>Type</th>
                                            <th style={styles.th}>Content</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {newsletterMessages.messages.map((message) => (
                                            <tr key={message.id}>
                                                <td style={styles.td}>
                                                    {message.message_datetime ? new Date(message.message_datetime).toLocaleString() : '—'}
                                                </td>
                                                <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12 }}>
                                                    {message.remote_jid}
                                                </td>
                                                <td style={styles.td}>{message.push_name || message.participant || '—'}</td>
                                                <td style={styles.td}>{message.message_type || '—'}</td>
                                                <td style={{ ...styles.td, maxWidth: 420, whiteSpace: 'pre-wrap' }}>
                                                    {message.text || '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            ) : activeTab === 'beta' ? (
                <>
                    <div style={styles.card}>
                        <h2 style={styles.subheading}>Beta Config</h2>
                        <form onSubmit={handleBetaConfigSave} style={styles.form}>
                            <div style={styles.row}>
                                <label style={styles.label}>Total Tester Cap (iOS + Android combined)</label>
                                <input
                                    type="number"
                                    value={safeBetaConfig.cap}
                                    onChange={(e) => setBetaConfig({ ...safeBetaConfig, cap: e.target.value })}
                                    style={{ ...styles.input, width: 100 }}
                                    min={0}
                                    required
                                />
                            </div>
                            <div style={styles.row}>
                                <label style={styles.label}>iOS TestFlight Link</label>
                                <input
                                    value={safeBetaConfig.ios_testflight_link}
                                    onChange={(e) => setBetaConfig({ ...safeBetaConfig, ios_testflight_link: e.target.value })}
                                    style={styles.input}
                                    placeholder="https://testflight.apple.com/join/..."
                                />
                            </div>
                            <div style={styles.row}>
                                <label style={styles.label}>Android Play Store Link</label>
                                <input
                                    value={safeBetaConfig.android_play_store_link}
                                    onChange={(e) => setBetaConfig({ ...safeBetaConfig, android_play_store_link: e.target.value })}
                                    style={styles.input}
                                    placeholder="https://play.google.com/..."
                                />
                            </div>
                            <div style={styles.inlineRow}>
                                <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                                    <input
                                        type="checkbox"
                                        checked={safeBetaConfig.ios_signups_enabled}
                                        onChange={(e) => setBetaConfig({ ...safeBetaConfig, ios_signups_enabled: e.target.checked })}
                                    />
                                    iOS signups open
                                </label>
                                <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                                    <input
                                        type="checkbox"
                                        checked={safeBetaConfig.android_signups_enabled}
                                        onChange={(e) => setBetaConfig({ ...safeBetaConfig, android_signups_enabled: e.target.checked })}
                                    />
                                    Android signups open
                                </label>
                            </div>
                            {betaConfigError && <p style={styles.error}>{betaConfigError}</p>}
                            {betaConfigSuccess && <p style={styles.success}>{betaConfigSuccess}</p>}
                            <div>
                                <button type="submit" style={styles.btn} disabled={betaConfigSaving}>
                                    {betaConfigSaving ? 'Saving…' : 'Save Config'}
                                </button>
                            </div>
                        </form>
                    </div>

                    <div style={styles.card}>
                        <div style={styles.tableHeader}>
                            <h2 style={styles.subheading}>Beta Signups</h2>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {selectedSignupIds.size > 0 && (
                                    <>
                                        <button
                                            onClick={handleBulkPromote}
                                            style={{ ...styles.btn, ...styles.btnSm }}
                                            disabled={bulkPromoting}
                                        >
                                            {bulkPromoting ? 'Promoting…' : `Promote ${selectedSignupIds.size} selected`}
                                        </button>
                                        <button
                                            onClick={handleBulkDelete}
                                            style={{ ...styles.btn, ...styles.btnSm, ...styles.btnDanger }}
                                            disabled={deleting}
                                        >
                                            {deleting ? 'Deleting…' : `Delete ${selectedSignupIds.size} selected`}
                                        </button>
                                    </>
                                )}
                                <button
                                    onClick={handlePromoteAll}
                                    style={{ ...styles.btn, ...styles.btnSm }}
                                    disabled={promotingAll}
                                >
                                    {promotingAll ? 'Promoting…' : 'Promote All Waitlist'}
                                </button>
                                <button
                                    onClick={() => { setSelectedSignupIds(new Set()); fetchSignups(secret); }}
                                    style={{ ...styles.btn, ...styles.btnSm, ...styles.btnSecondary }}
                                >
                                    Refresh
                                </button>
                            </div>
                        </div>

                        {safeSignupsData && (
                            <p style={{ margin: '0 0 12px', fontSize: 13, color: '#555' }}>
                                Testers: <strong>{safeSignupsData.ios_testers + safeSignupsData.android_testers}</strong>/{safeBetaConfig.cap}
                                &nbsp;&nbsp;|&nbsp;&nbsp;
                                iOS: <strong>{safeSignupsData.ios_testers}</strong> testers · <strong>{safeSignupsData.ios_waitlist}</strong> waitlisted
                                &nbsp;&nbsp;|&nbsp;&nbsp;
                                Android: <strong>{safeSignupsData.android_testers}</strong> testers · <strong>{safeSignupsData.android_waitlist}</strong> waitlisted
                                &nbsp;&nbsp;|&nbsp;&nbsp;
                                Registered: <strong>{safeSignupsData.registered_count}</strong>
                                {signups.length > 0 && (
                                    <> ({Math.round(safeSignupsData.registered_count / signups.length * 100)}%)</>
                                )}
                                &nbsp;&nbsp;|&nbsp;&nbsp;
                                Open Instances: <strong>{safeSignupsData.open_instances}</strong>
                            </p>
                        )}

                        {signupsError && <p style={styles.error}>{signupsError}</p>}

                        {safeSignupsData && signups.length === 0 && (
                            <p style={{ color: '#888' }}>No signups yet.</p>
                        )}

                        {safeSignupsData && signups.length > 0 && (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={{ ...styles.th, width: 32 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedSignupIds.size === signups.length}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedSignupIds(new Set(signups.map(s => s.id)));
                                                        } else {
                                                            setSelectedSignupIds(new Set());
                                                        }
                                                    }}
                                                />
                                            </th>
                                            <th style={styles.th}>Email</th>
                                            <th style={styles.th}>Phone</th>
                                            <th style={{ ...styles.th, textAlign: 'center' }}>Registered</th>
                                            <th style={{ ...styles.th, textAlign: 'center' }}>Connected</th>
                                            <th style={styles.th}>Platform</th>
                                            <th style={styles.th}>Group</th>
                                            <th style={styles.th}>Emails Sent</th>
                                            <th style={styles.th}>Signed Up</th>
                                            <th style={styles.th}>Promoted</th>
                                            <th style={styles.th}>Reassign</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {signups.map((s) => (
                                            <tr key={s.id} style={selectedSignupIds.has(s.id) ? styles.activeRow : {}}>
                                                <td style={styles.td}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedSignupIds.has(s.id)}
                                                        onChange={(e) => {
                                                            const next = new Set(selectedSignupIds);
                                                            if (e.target.checked) next.add(s.id);
                                                            else next.delete(s.id);
                                                            setSelectedSignupIds(next);
                                                        }}
                                                    />
                                                </td>
                                                <td style={styles.td}>{s.email}</td>
                                                <td style={styles.td}>{s.phone_number || '—'}</td>
                                                <td style={{ ...styles.td, textAlign: 'center', color: s.registered ? '#22c55e' : '#94a3b8' }}>{s.registered ? '✓' : '—'}</td>
                                                <td style={{ ...styles.td, textAlign: 'center', color: s.connected ? '#22c55e' : '#94a3b8' }}>{s.connected ? '✓' : '—'}</td>
                                                <td style={styles.td}>{s.platform}</td>
                                                <td style={styles.td}>
                                                    <span style={groupBadgeStyle(s.google_group)}>
                                                        {shortGroup(s.google_group)}
                                                    </span>
                                                </td>
                                                <td style={{ ...styles.td, textAlign: 'center' }}>{s.emails_sent}</td>
                                                <td style={styles.td}>{new Date(s.created_at).toLocaleDateString()}</td>
                                                <td style={styles.td}>{s.promoted_at ? new Date(s.promoted_at).toLocaleDateString() : '—'}</td>
                                                <td style={styles.td}>
                                                    <select
                                                        value={s.google_group || ''}
                                                        onChange={(e) => handleReassign(s.id, e.target.value)}
                                                        disabled={reassigning[s.id]}
                                                        style={styles.select}
                                                    >
                                                        <option value="" disabled>—</option>
                                                        {ALL_GROUPS.map((g) => (
                                                            <option key={g} value={g}>{g}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <>
                    {loadError && <p style={styles.error}>{loadError}</p>}

                    <div style={styles.card}>
                        <h2 style={styles.subheading}>{editingId ? `Editing entry #${editingId}` : `New ${docConfig.title} Entry`}</h2>
                        <form onSubmit={handleSave} style={styles.form}>
                            <div style={styles.row}>
                                <label style={styles.label}>App</label>
                                <select
                                    value={form.app}
                                    onChange={(e) => setForm({ ...form, app: e.target.value })}
                                    style={styles.input}
                                    disabled={!!editingId}
                                    required={!editingId}
                                >
                                    <option value="happtrak">HappTrak</option>
                                    <option value="jcal">JCAL</option>
                                </select>
                            </div>
                            <div style={styles.row}>
                                <label style={styles.label}>Version</label>
                                <input
                                    type="number"
                                    value={form.version}
                                    onChange={(e) => setForm({ ...form, version: e.target.value })}
                                    style={styles.input}
                                    disabled={!!editingId}
                                    required={!editingId}
                                />
                            </div>
                            <div style={styles.row}>
                                <label style={styles.label}>Language</label>
                                <input
                                    value={form.language}
                                    onChange={(e) => setForm({ ...form, language: e.target.value })}
                                    style={styles.input}
                                    disabled={!!editingId}
                                    required={!editingId}
                                />
                            </div>
                            <div style={styles.row}>
                                <label style={styles.label}>Jurisdiction</label>
                                <input
                                    value={form.jurisdiction}
                                    onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
                                    style={styles.input}
                                    disabled={!!editingId}
                                    required={!editingId}
                                />
                            </div>
                            <div style={styles.row}>
                                <label style={styles.label}>Effective Date</label>
                                <input
                                    type="datetime-local"
                                    value={form.effective_date}
                                    onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                                    style={styles.input}
                                    required
                                />
                            </div>
                            <div style={styles.row}>
                                <label style={styles.label}>Content (Markdown)</label>
                                <div data-color-mode="light">
                                    <MDEditor
                                        value={form.content}
                                        onChange={(val) => setForm({ ...form, content: val || '' })}
                                        height={400}
                                        preview="live"
                                    />
                                </div>
                            </div>
                            {formError && <p style={styles.error}>{formError}</p>}
                            {formSuccess && <p style={styles.success}>{formSuccess}</p>}
                            <div style={styles.formActions}>
                                <button type="submit" style={styles.btn} disabled={saving}>
                                    {saving ? 'Saving…' : editingId ? 'Update' : 'Create'}
                                </button>
                                {editingId && (
                                    <button type="button" style={{ ...styles.btn, ...styles.btnSecondary }} onClick={startCreate}>
                                        Cancel
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>

                    <div style={styles.card}>
                        <div style={styles.tableHeader}>
                            <h2 style={styles.subheading}>All {docConfig.shortLabel} Entries</h2>
                            <button onClick={startCreate} style={{ ...styles.btn, ...styles.btnSm }}>+ New</button>
                        </div>
                        {entries.length === 0 ? (
                            <p style={{ color: '#888' }}>No entries yet.</p>
                        ) : (
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>ID</th>
                                        <th style={styles.th}>App</th>
                                        <th style={styles.th}>Version</th>
                                        <th style={styles.th}>Language</th>
                                        <th style={styles.th}>Jurisdiction</th>
                                        <th style={styles.th}>Effective Date</th>
                                        <th style={styles.th}>Created</th>
                                        <th style={styles.th}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.map((entry) => (
                                        <tr key={entry.id} style={editingId === entry.id ? styles.activeRow : {}}>
                                            <td style={styles.td}>{entry.id}</td>
                                            <td style={styles.td}>{entry.app}</td>
                                            <td style={styles.td}>{entry.version}</td>
                                            <td style={styles.td}>{entry.language}</td>
                                            <td style={styles.td}>{entry.jurisdiction}</td>
                                            <td style={styles.td}>{new Date(entry.effective_date).toLocaleString()}</td>
                                            <td style={styles.td}>{entry.created_at ? new Date(entry.created_at).toLocaleString() : '—'}</td>
                                            <td style={styles.td}>
                                                <button onClick={() => startEdit(entry)} style={{ ...styles.btn, ...styles.btnSm }}>Edit</button>
                                                {' '}
                                                <button onClick={() => handleDelete(entry.id)} style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSm }}>Delete</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

/**
 * Events the pipeline understood but could not date.
 *
 * The extractor used to invent a date when the message stated none, publishing events
 * that started the moment the message arrived. Those extractions are now parked in
 * pending_events instead — fully enriched, missing only a start time. Supplying one here
 * publishes the event with all that enrichment intact.
 */
// The ResizeImage flow step stores every WhatsApp flyer at 40% of its original height
// (src/flowmaker/steps/resizeimage.py), so the copy the queue holds is vertically
// squashed and its small print — dates, times, addresses — is hard to read. Reviewing
// is exactly when that print matters, so the lightbox undoes the squash. Only images
// written by that step carry this path, so nothing else gets stretched.
const RESIZED_UPLOAD_PATH = 'evolution-api/resized_';
const RESIZE_HEIGHT_FACTOR = 0.4;
const isSquashedUpload = (src) => !!src && src.includes(RESIZED_UPLOAD_PATH);

function PendingEventsReview({ apiUrl, headers, styles }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [drafts, setDrafts] = useState({});   // id -> { start, end, title }
    const [busy, setBusy] = useState({});       // id -> bool
    const [expanded, setExpanded] = useState({});
    const [zoomed, setZoomed] = useState(null); // { src, fallback, title } of the flyer viewed
    const [zoomNatural, setZoomNatural] = useState(null); // { w, h } once the flyer loads
    const [unsquash, setUnsquash] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axios.get(`${apiUrl}/admin/pending-events`, {
                headers, params: { status: 'pending', limit: 200 }, timeout: 20000,
            });
            setRows(res.data || []);
        } catch (e) {
            setError(e.response?.data?.detail || 'Could not load the review queue');
        } finally {
            setLoading(false);
        }
    }, [apiUrl, headers]);

    useEffect(() => { load(); }, [load]);

    // Escape closes the flyer lightbox, matching what the overlay's backdrop click does.
    useEffect(() => {
        if (!zoomed) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setZoomed(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [zoomed]);

    // Width over the height the flyer had before the step squashed it; undefined leaves
    // the image alone (not a resized upload, correction toggled off, or not loaded yet).
    const zoomAspect = (zoomed && unsquash && zoomNatural && isSquashedUpload(zoomed.src))
        ? zoomNatural.w / (zoomNatural.h / RESIZE_HEIGHT_FACTOR)
        : undefined;

    const draftFor = (id) => drafts[id] || { start: '', end: '', title: '' };
    const setDraft = (id, patch) =>
        setDrafts((d) => ({ ...d, [id]: { ...draftFor(id), ...patch } }));

    const publish = async (row) => {
        const draft = draftFor(row.id);
        if (!draft.start) {
            setError('A start time is required to publish');
            return;
        }
        setBusy((b) => ({ ...b, [row.id]: true }));
        setError('');
        setSuccess('');
        try {
            // datetime-local yields a naive string; the API rejects those on purpose, so
            // send the browser's real offset rather than letting the server guess.
            const payload = { start_time: new Date(draft.start).toISOString() };
            if (draft.end) payload.end_time = new Date(draft.end).toISOString();
            if (draft.title && draft.title !== row.title) payload.title = draft.title;

            const res = await axios.post(
                `${apiUrl}/admin/pending-events/${row.id}/publish`, payload,
                { headers, timeout: 30000 },
            );
            setSuccess(`Published "${res.data.title}"`);
            setRows((current) => current.filter((r) => r.id !== row.id));
        } catch (e) {
            setError(e.response?.data?.detail || 'Publish failed');
        } finally {
            setBusy((b) => ({ ...b, [row.id]: false }));
        }
    };

    const discard = async (row) => {
        setBusy((b) => ({ ...b, [row.id]: true }));
        setError('');
        setSuccess('');
        try {
            await axios.post(`${apiUrl}/admin/pending-events/${row.id}/discard`, {},
                { headers, timeout: 20000 });
            setSuccess(`Discarded "${row.title}"`);
            setRows((current) => current.filter((r) => r.id !== row.id));
        } catch (e) {
            setError(e.response?.data?.detail || 'Discard failed');
        } finally {
            setBusy((b) => ({ ...b, [row.id]: false }));
        }
    };

    const where = (row) => {
        const parts = [row.raw_location_text, row.location_city_display].filter(Boolean);
        return parts.length ? parts.join(', ') : '—';
    };

    return (
        <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <h2 style={{ ...styles.subheading, marginBottom: 0 }}>
                    Needs a Date {rows.length > 0 ? `(${rows.length})` : ''}
                </h2>
                <button type="button" onClick={load} disabled={loading} style={styles.button}>
                    {loading ? 'Loading…' : 'Refresh'}
                </button>
            </div>
            <p style={{ color: '#666', marginTop: 8 }}>
                Events extracted from JCAL groups whose message stated no date. Everything
                else — venue, location, image — is already resolved.
            </p>

            {error && <p style={styles.error}>{error}</p>}
            {success && <p style={styles.success}>{success}</p>}

            {!loading && rows.length === 0 && (
                <p style={{ color: '#666' }}>Nothing waiting for review.</p>
            )}

            {rows.map((row) => {
                const draft = draftFor(row.id);
                const isBusy = !!busy[row.id];
                return (
                    <div key={row.id} style={{
                        border: '1px solid #ddd', borderRadius: 6,
                        padding: 12, marginTop: 12,
                    }}>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            {(row.resized_image_url || row.event_image_url) && (
                                // The thumbnail crops the flyer square, so the details that
                                // decide the date are usually unreadable — click through to
                                // the full frame. resized_image_url is the one that opens:
                                // event_image_url is the raw Evolution media URL, which the
                                // browser cannot fetch.
                                <img
                                    src={row.resized_image_url || row.event_image_url}
                                    alt=""
                                    title="Click to enlarge"
                                    onClick={() => {
                                        setZoomNatural(null);
                                        setUnsquash(true);
                                        setZoomed({
                                            src: row.resized_image_url || row.event_image_url,
                                            fallback: row.event_image_url || row.resized_image_url,
                                            title: row.title,
                                        });
                                    }}
                                    style={{
                                        width: 96, height: 96, objectFit: 'cover',
                                        borderRadius: 4, cursor: 'zoom-in',
                                    }}
                                />
                            )}
                            <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                                <strong>{row.title}</strong>
                                <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>
                                    <div>Where: {where(row)}</div>
                                    <div>Group: {row.group_name || '—'}</div>
                                    {row.extraction_rationale && (
                                        <div style={{ fontStyle: 'italic', marginTop: 4 }}>
                                            {row.extraction_rationale}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setExpanded((x) => ({ ...x, [row.id]: !x[row.id] }))}
                                    style={{ ...styles.button, marginTop: 8 }}
                                >
                                    {expanded[row.id] ? 'Hide message' : 'Show message'}
                                </button>
                                {expanded[row.id] && (
                                    <pre style={{
                                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                        background: '#f6f6f6', padding: 8, borderRadius: 4,
                                        marginTop: 8, fontSize: 12,
                                    }}>
                                        {row.message_text || '(no message text)'}
                                    </pre>
                                )}
                            </div>
                        </div>

                        <div style={{ ...styles.inlineRow, marginTop: 12, alignItems: 'flex-end' }}>
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>Start *</label>
                                <input
                                    type="datetime-local"
                                    value={draft.start}
                                    onChange={(e) => setDraft(row.id, { start: e.target.value })}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>End (optional)</label>
                                <input
                                    type="datetime-local"
                                    value={draft.end}
                                    onChange={(e) => setDraft(row.id, { end: e.target.value })}
                                    style={styles.input}
                                />
                            </div>
                            <div style={{ ...styles.fieldGroup, flex: '1 1 220px' }}>
                                <label style={styles.label}>Title</label>
                                <input
                                    type="text"
                                    value={draft.title || row.title}
                                    onChange={(e) => setDraft(row.id, { title: e.target.value })}
                                    style={styles.input}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => publish(row)}
                                disabled={isBusy || !draft.start}
                                style={styles.button}
                            >
                                {isBusy ? 'Working…' : 'Publish'}
                            </button>
                            <button
                                type="button"
                                onClick={() => discard(row)}
                                disabled={isBusy}
                                style={styles.button}
                            >
                                Discard
                            </button>
                        </div>
                    </div>
                );
            })}

            {zoomed && (
                <div
                    onClick={() => setZoomed(null)}
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
                        zIndex: 1200, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', padding: 24,
                        cursor: 'zoom-out',
                    }}
                >
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        color: '#fff', marginBottom: 12, maxWidth: '90vw',
                    }}>
                        <span style={{ fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {zoomed.title}
                        </span>
                        {isSquashedUpload(zoomed.src) && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setUnsquash((u) => !u); }}
                                style={{
                                    background: 'none', border: '1px solid #666', color: '#fff',
                                    borderRadius: 4, fontSize: 12, padding: '2px 8px',
                                    cursor: 'pointer', whiteSpace: 'nowrap',
                                }}
                            >
                                {unsquash ? 'Show as stored' : 'Fix aspect'}
                            </button>
                        )}
                        <a
                            href={zoomed.src}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: '#9ecbff', fontSize: 13 }}
                        >
                            Open full size ↗
                        </a>
                        <button
                            type="button"
                            onClick={() => setZoomed(null)}
                            style={{
                                background: 'none', border: 'none', color: '#fff',
                                fontSize: 24, lineHeight: 1, cursor: 'pointer',
                            }}
                        >
                            ×
                        </button>
                    </div>
                    <img
                        src={zoomed.src}
                        alt={zoomed.title}
                        // Stop the backdrop handler so a click on the flyer does not close it.
                        onClick={(e) => e.stopPropagation()}
                        onLoad={(e) => setZoomNatural({
                            w: e.target.naturalWidth, h: e.target.naturalHeight,
                        })}
                        // event_image_url is usually unreachable, but when the resized copy
                        // is the one that 404s it is the only thing left to try.
                        // Promote the fallback into state rather than assigning to the DOM
                        // node: the next render would put the failing src straight back.
                        // Clearing it as we go also stops this retrying forever.
                        onError={() => setZoomed((z) => (
                            z && z.fallback && z.fallback !== z.src
                                ? { ...z, src: z.fallback, fallback: null }
                                : z
                        ))}
                        style={{
                            width: 'auto', height: 'auto',
                            maxWidth: '90vw', maxHeight: '85vh',
                            background: '#fff', borderRadius: 4, cursor: 'default',
                            ...(zoomAspect
                                ? { aspectRatio: String(zoomAspect), objectFit: 'fill' }
                                : { objectFit: 'contain' }),
                        }}
                    />
                </div>
            )}
        </div>
    );
}

function JcalPanel({
    config, error, success, loading,
    groups, users, search, userId, page, saving,
    headers, apiUrl, onRefresh,
    setError, setSuccess,
    setSearch, setUserId, setPage, setSaving, setGroups,
    sortBy, sortDir, setSortBy, setSortDir,
    fetchConfig, secret,
    styles,
    jcalSettings, setJcalSettings,
    jcalSettingsError, jcalSettingsSuccess, jcalSettingsSaving,
    onJcalSettingsSave,
    newsletters, setNewsletters,
    syncPanel, setSyncPanel,
    syncStats, setSyncStats,
    syncSinceDays, setSyncSinceDays,
    launching, setLaunching,
    purging, setPurging,
    activeJobs, setActiveJobs,
}) {
    const collectionKeys = (config?.collections || []).map((c) => c.key);

    const handleSort = (key) => {
        const newDir = sortBy === key ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
        setSortBy(key);
        setSortDir(newDir);
        setPage(1);
        fetchConfig(secret, 1, search, userId, key, newDir);
    };

    const sortIndicator = (key) => {
        if (sortBy !== key) return ' ↕';
        return sortDir === 'asc' ? ' ↑' : ' ↓';
    };

    const thSortable = {
        ...styles.th,
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap',
    };

    const handleToggle = async (group, collectionKey, checked) => {
        const key = `${group.group_id}::${collectionKey}`;
        setSaving((current) => ({ ...current, [key]: true }));
        setError('');
        setSuccess('');
        try {
            const url = `${apiUrl}/admin/apps/jcal/collections/${collectionKey}/groups/${encodeURIComponent(group.group_id)}`;
            if (checked) {
                await axios.put(url, {}, { headers, timeout: 30000 });
            } else {
                await axios.delete(url, { headers, timeout: 30000 });
                if (syncPanel?.group?.group_id === group.group_id && syncPanel?.collectionKey === collectionKey) {
                    setSyncPanel(null);
                }
            }
            setSuccess(`${group.group_name || group.group_id} updated.`);
            const updatedCollections = (cols) =>
                checked ? [...cols, collectionKey] : cols.filter((k) => k !== collectionKey);
            const updateGroup = (g) => g.group_id === group.group_id
                ? { ...g, collections: updatedCollections(g.collections) }
                : g;
            if (group.is_newsletter) {
                setNewsletters((prev) => prev.map(updateGroup));
            } else {
                setGroups((prev) => ({ ...prev, groups: prev.groups.map(updateGroup) }));
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to update group collection.');
        } finally {
            setSaving((current) => ({ ...current, [key]: false }));
        }
    };

    const handleOpenSync = async (group, collectionKey) => {
        const key = `${group.group_id}::${collectionKey}`;
        if (syncPanel?.group?.group_id === group.group_id && syncPanel?.collectionKey === collectionKey) {
            setSyncPanel(null);
            return;
        }
        setSyncPanel({ group, collectionKey });
        setSyncStats((prev) => ({ ...prev, [key]: { loading: true, available: null } }));
        try {
            const sinceDays = syncSinceDays[key] ?? 30;
            const res = await axios.get(
                `${apiUrl}/admin/apps/jcal/collections/${collectionKey}/groups/${encodeURIComponent(group.group_id)}/sync-stats`,
                { headers, params: { since_days: sinceDays }, timeout: 15000 }
            );
            setSyncStats((prev) => ({ ...prev, [key]: { loading: false, available: res.data.available } }));
        } catch {
            setSyncStats((prev) => ({ ...prev, [key]: { loading: false, available: null, error: true } }));
        }
    };

    const handleLaunchSync = async (group, collectionKey) => {
        const key = `${group.group_id}::${collectionKey}`;
        setLaunching((prev) => ({ ...prev, [key]: true }));
        setError('');
        setSuccess('');
        try {
            const sinceDays = syncSinceDays[key] ?? 30;
            const res = await axios.post(
                `${apiUrl}/admin/apps/jcal/collections/${collectionKey}/groups/${encodeURIComponent(group.group_id)}/sync`,
                { since_days: sinceDays },
                { headers, timeout: 30000 }
            );
            const { job_id, total_messages } = res.data;
            setActiveJobs((prev) => ({
                ...prev,
                [key]: { job_id, total: total_messages, completed: 0, status: 'in_progress' },
            }));
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to launch sync.');
        } finally {
            setLaunching((prev) => ({ ...prev, [key]: false }));
        }
    };

    const handleLaunchCollectionSync = async (collectionKey) => {
        const key = `collection::${collectionKey}`;
        setLaunching((prev) => ({ ...prev, [key]: true }));
        setError('');
        setSuccess('');
        try {
            const sinceDays = syncSinceDays[key] ?? 30;
            const res = await axios.post(
                `${apiUrl}/admin/apps/jcal/collections/${collectionKey}/sync`,
                { since_days: sinceDays },
                { headers, timeout: 30000 }
            );
            const { meta_job_id, total_subscriptions, total_messages } = res.data;
            setActiveJobs((prev) => ({
                ...prev,
                [key]: {
                    meta_job_id,
                    total_subscriptions,
                    total: total_messages,
                    completed: 0,
                    status: meta_job_id ? 'in_progress' : 'nothing_to_sync',
                },
            }));
            setSuccess(
                meta_job_id
                    ? `Started ${collectionKey.toUpperCase()} meta sync for ${total_subscriptions} group${total_subscriptions !== 1 ? 's' : ''}.`
                    : `No eligible messages to sync for ${collectionKey.toUpperCase()}.`
            );
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to launch collection sync.');
        } finally {
            setLaunching((prev) => ({ ...prev, [key]: false }));
        }
    };

    const handleDismissJob = (group, collectionKey) => {
        const key = `${group.group_id}::${collectionKey}`;
        setActiveJobs((prev) => { const next = { ...prev }; delete next[key]; return next; });
        setSyncPanel(null);
    };

    const handlePurge = async (group, collectionKey) => {
        const key = `${group.group_id}::${collectionKey}`;
        const count = group.calendar_event_counts?.[collectionKey] ?? 0;
        if (!window.confirm(`Purge ${count} CalendarEvent record${count !== 1 ? 's' : ''} for "${group.group_name || group.group_id}" from ${collectionKey.toUpperCase()}? This cannot be undone.`)) return;
        setPurging((prev) => ({ ...prev, [key]: true }));
        setError('');
        setSuccess('');
        try {
            const res = await axios.delete(
                `${apiUrl}/admin/apps/jcal/collections/${collectionKey}/groups/${encodeURIComponent(group.group_id)}/events`,
                { headers, timeout: 30000 }
            );
            setSuccess(`Purged ${res.data.deleted} event${res.data.deleted !== 1 ? 's' : ''}.`);
            const updateGroup = (g) => g.group_id === group.group_id
                ? { ...g, calendar_event_counts: { ...g.calendar_event_counts, [collectionKey]: 0 } }
                : g;
            if (group.is_newsletter) {
                setNewsletters((prev) => prev.map(updateGroup));
            } else {
                setGroups((prev) => ({ ...prev, groups: prev.groups.map(updateGroup) }));
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to purge events.');
        } finally {
            setPurging((prev) => ({ ...prev, [key]: false }));
        }
    };

    if (!config) {
        return (
            <div style={styles.card}>
                {error ? <p style={styles.error}>{error}</p> : <p style={{ color: '#888' }}>{loading ? 'Loading…' : 'JCAL config has not loaded.'}</p>}
                {!loading && <button type="button" style={styles.btn} onClick={onRefresh}>Retry</button>}
            </div>
        );
    }

    return (
        <>
            {error && <p style={styles.error}>{error}</p>}
            {success && <p style={styles.success}>{success}</p>}
            <PendingEventsReview apiUrl={apiUrl} headers={headers} styles={styles} />
            <div style={styles.card}>
                <h2 style={styles.subheading}>JCAL App Settings</h2>
                <form onSubmit={onJcalSettingsSave} style={styles.form}>
                    <div style={styles.inlineRow}>
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>Event Feedback Probability (0–1)</label>
                            <input
                                type="number"
                                value={jcalSettings.event_feedback_prompt_probability}
                                onChange={(e) => setJcalSettings({ ...jcalSettings, event_feedback_prompt_probability: e.target.value })}
                                style={{ ...styles.input, width: 100 }}
                                min={0} max={1} step={0.01} required
                            />
                        </div>
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>Interstitial Ad Probability (0–1)</label>
                            <input
                                type="number"
                                value={jcalSettings.interstitial_ad_probability}
                                onChange={(e) => setJcalSettings({ ...jcalSettings, interstitial_ad_probability: e.target.value })}
                                style={{ ...styles.input, width: 100 }}
                                min={0} max={1} step={0.01} required
                            />
                        </div>
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>Ad Mode</label>
                            <select
                                value={jcalSettings.ad_gate_mode ?? 'random'}
                                onChange={(e) => setJcalSettings({ ...jcalSettings, ad_gate_mode: e.target.value })}
                                style={{ ...styles.input, width: 220 }}
                            >
                                <option value="random">Random interstitial</option>
                                <option value="gate">Gate every 1/probability opens</option>
                            </select>
                        </div>
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>Gate Rollout (% of users)</label>
                            <input
                                type="number"
                                value={jcalSettings.ad_gate_rollout_pct ?? 100}
                                onChange={(e) => setJcalSettings({ ...jcalSettings, ad_gate_rollout_pct: e.target.value })}
                                style={{ ...styles.input, width: 100 }}
                                min={0} max={100} step={1} required
                            />
                        </div>
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>Gate Every N Active Days (0 = off)</label>
                            <input
                                type="number"
                                value={jcalSettings.ad_gate_active_days ?? 5}
                                onChange={(e) => setJcalSettings({ ...jcalSettings, ad_gate_active_days: e.target.value })}
                                style={{ ...styles.input, width: 100 }}
                                min={0} max={365} step={1} required
                            />
                        </div>
                    </div>
                    <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: 12, marginTop: 4 }}>
                        <p style={{ margin: '0 0 10px', fontSize: 13, color: '#555', fontWeight: 600 }}>Friend Suggestions</p>
                        <div style={styles.inlineRow}>
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>Min Common Views</label>
                                <input
                                    type="number"
                                    value={jcalSettings.friend_suggestion_threshold}
                                    onChange={(e) => setJcalSettings({ ...jcalSettings, friend_suggestion_threshold: e.target.value })}
                                    style={{ ...styles.input, width: 100 }}
                                    min={1} step={1} required
                                />
                            </div>
                        </div>
                    </div>
                    <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: 12, marginTop: 4 }}>
                        <p style={{ margin: '0 0 10px', fontSize: 13, color: '#555', fontWeight: 600 }}>Rabbi Chatbot Limits</p>
                        <div style={styles.inlineRow}>
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>Session Budget (USD)</label>
                                <input
                                    type="number"
                                    value={jcalSettings.voice_session_budget_usd}
                                    onChange={(e) => setJcalSettings({ ...jcalSettings, voice_session_budget_usd: e.target.value })}
                                    style={{ ...styles.input, width: 100 }}
                                    min={0} step={0.01} required
                                />
                            </div>
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>Max OpenAI Tokens / Response</label>
                                <input
                                    type="number"
                                    value={jcalSettings.voice_session_max_openai_tokens}
                                    onChange={(e) => setJcalSettings({ ...jcalSettings, voice_session_max_openai_tokens: e.target.value })}
                                    style={{ ...styles.input, width: 120 }}
                                    min={1} step={1} required
                                />
                            </div>
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>Daily Session Limit / User</label>
                                <input
                                    type="number"
                                    value={jcalSettings.voice_daily_session_limit}
                                    onChange={(e) => setJcalSettings({ ...jcalSettings, voice_daily_session_limit: e.target.value })}
                                    style={{ ...styles.input, width: 100 }}
                                    min={1} step={1} required
                                />
                            </div>
                        </div>
                    </div>
                    {jcalSettingsError && <p style={styles.error}>{jcalSettingsError}</p>}
                    {jcalSettingsSuccess && <p style={styles.success}>{jcalSettingsSuccess}</p>}
                    <div>
                        <button type="submit" style={styles.btn} disabled={jcalSettingsSaving}>
                            {jcalSettingsSaving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </form>
            </div>
            <div style={styles.card}>
                <div style={styles.tableHeader}>
                    <h2 style={styles.subheading}>JCAL Collections</h2>
                    <button type="button" style={{ ...styles.btn, ...styles.btnSm }} onClick={onRefresh} disabled={loading}>
                        Refresh
                    </button>
                </div>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    {config.collections.map((collection) => {
                        const syncKey = `collection::${collection.key}`;
                        const collectionJob = activeJobs[syncKey];
                        return (
                        <div key={collection.key} style={{ minWidth: 220 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                                <strong>{collection.display_name}</strong>
                                {collection.calendar_id && (
                                    <button
                                        type="button"
                                        style={{ ...styles.btn, ...styles.btnSm }}
                                        onClick={() => handleLaunchCollectionSync(collection.key)}
                                        disabled={Boolean(launching[syncKey])}
                                    >
                                        {launching[syncKey] ? 'Starting…' : 'Sync all'}
                                    </button>
                                )}
                            </div>
                            <div style={{ fontSize: 13, color: '#666' }}>
                                {collection.group_count} groups
                                {collection.event_count != null ? ` · ${collection.event_count} events` : ''}
                            </div>
                            {collection.calendar_id && (
                                <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                                    Since
                                    <input
                                        type="number"
                                        min={1}
                                        max={365}
                                        value={syncSinceDays[syncKey] ?? 30}
                                        onChange={(e) => setSyncSinceDays((prev) => ({
                                            ...prev,
                                            [syncKey]: Number(e.target.value) || 30,
                                        }))}
                                        style={{ ...styles.input, width: 70, padding: '4px 6px' }}
                                    />
                                    days
                                </label>
                            )}
                            {collectionJob && (
                                <div style={{ fontSize: 12, color: '#555', marginTop: 6 }}>
                                    {collectionJob.meta_job_id ? (
                                        <>
                                            Meta job {collectionJob.meta_job_id}
                                            <br />
                                            {collectionJob.total_subscriptions} groups · {collectionJob.total} messages
                                        </>
                                    ) : (
                                        'No eligible messages.'
                                    )}
                                </div>
                            )}
                            {collection.calendar_id && (
                                <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#888' }}>{collection.calendar_id}</div>
                            )}
                        </div>
                        );
                    })}
                </div>
            </div>
            <InstagramAccountsPanel
                apiUrl={apiUrl}
                headers={headers}
                styles={styles}
                collections={config.collections}
            />
            <CalendarFeedsPanel
                apiUrl={apiUrl}
                headers={headers}
                styles={styles}
                collections={config.collections}
            />
            <div style={styles.card}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    <input
                        placeholder="Search group ID or name"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                setPage(1);
                                fetchConfig(secret, 1, search, userId, sortBy, sortDir);
                            }
                        }}
                        style={{ ...styles.input, width: 280 }}
                    />
                    <select value={userId} onChange={(e) => {
                        const value = e.target.value;
                        setUserId(value);
                        setPage(1);
                        fetchConfig(secret, 1, search, value, sortBy, sortDir);
                    }} style={styles.select}>
                        <option value="">All HappTrak users</option>
                        {users.map((user) => <option key={user.id} value={user.id}>{user.email}</option>)}
                    </select>
                    <button type="button" style={styles.btn} onClick={() => {
                        setPage(1);
                        fetchConfig(secret, 1, search, userId, sortBy, sortDir);
                    }}>Search</button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={thSortable} onClick={() => handleSort('name')}>Group{sortIndicator('name')}</th>
                                <th style={thSortable} onClick={() => handleSort('size')}>Size{sortIndicator('size')}</th>
                                <th style={styles.th}>HappTrak Users</th>
                                {collectionKeys.map((key) => (
                                    <th key={key} style={{ ...thSortable, textAlign: 'center' }} onClick={() => handleSort(key)}>
                                        {key.toUpperCase()}{sortIndicator(key)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {[...newsletters.map((nl) => ({ ...nl, is_newsletter: true })), ...groups.groups].map((group) => {
                                const isNl = Boolean(group.is_newsletter);
                                const panelOpen = syncPanel?.group?.group_id === group.group_id;
                                const colSpan = 3 + collectionKeys.length;
                                return (
                                    <React.Fragment key={group.group_id}>
                                        <tr style={isNl ? { background: '#f0f7ff' } : {}}>
                                            <td style={styles.td}>
                                                {isNl && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                        <span style={{ background: '#1a73e8', color: '#fff', borderRadius: 4, fontSize: 10, fontWeight: 700, padding: '1px 5px', whiteSpace: 'nowrap' }}>Newsletter</span>
                                                        <span>{group.group_name || '—'}</span>
                                                    </div>
                                                )}
                                                {!isNl && <div>{group.group_name || '—'}</div>}
                                                <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#777' }}>{group.group_id}</div>
                                            </td>
                                            <td style={styles.td}>{isNl ? '—' : (group.size ?? '—')}</td>
                                            <td style={styles.td}>{isNl ? '—' : (group.users?.map((u) => u.email).join(', ') || '—')}</td>
                                            {collectionKeys.map((key) => {
                                                const saveKey = `${group.group_id}::${key}`;
                                                const isSelected = group.collections.includes(key);
                                                const linkedCount = group.calendar_event_counts?.[key] ?? 0;
                                                const hasStale = !isSelected && linkedCount > 0;
                                                const isPanelOpenForKey = panelOpen && syncPanel?.collectionKey === key;
                                                return (
                                                    <td key={key} style={{ ...styles.td, verticalAlign: 'top', paddingTop: 10 }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    disabled={Boolean(saving[saveKey])}
                                                                    onChange={(e) => handleToggle(group, key, e.target.checked)}
                                                                />
                                                                {isSelected && (
                                                                    <button
                                                                        style={{ ...collectionBtnStyle, background: isPanelOpenForKey ? '#1a73e8' : '#e8f0fe', color: isPanelOpenForKey ? '#fff' : '#1a73e8', borderColor: isPanelOpenForKey ? '#1a73e8' : '#c5d8fb' }}
                                                                        onClick={() => handleOpenSync(group, key)}
                                                                        disabled={Boolean(saving[saveKey])}
                                                                    >
                                                                        Sync {isPanelOpenForKey ? '▴' : '▾'}
                                                                    </button>
                                                                )}
                                                                {hasStale && (
                                                                    <button
                                                                        style={{ ...collectionBtnStyle, background: '#fce8e6', color: '#c5221f', borderColor: '#f5c6c4' }}
                                                                        onClick={() => handlePurge(group, key)}
                                                                        disabled={Boolean(purging[saveKey])}
                                                                    >
                                                                        {purging[saveKey] ? '…' : 'Purge'}
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {linkedCount > 0 && (
                                                                <span style={{ fontSize: 11, color: hasStale ? '#b45309' : '#888', paddingLeft: 2 }}>
                                                                    {linkedCount} linked
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                        {panelOpen && (
                                            <tr style={{ background: '#f0f6ff' }}>
                                                <td colSpan={colSpan} style={{ padding: 0, borderBottom: '2px solid #1a73e8' }}>
                                                    <SyncPanel
                                                        group={syncPanel.group}
                                                        collectionKey={syncPanel.collectionKey}
                                                        syncStats={syncStats}
                                                        setSyncStats={setSyncStats}
                                                        syncSinceDays={syncSinceDays}
                                                        setSyncSinceDays={setSyncSinceDays}
                                                        launching={launching}
                                                        onLaunch={handleLaunchSync}
                                                        onClose={() => setSyncPanel(null)}
                                                        activeJobs={activeJobs}
                                                        setActiveJobs={setActiveJobs}
                                                        onDismissJob={handleDismissJob}
                                                        headers={headers}
                                                        apiUrl={apiUrl}
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                    <span style={{ fontSize: 13, color: '#666' }}>{groups.total} groups</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" style={{ ...styles.btn, ...styles.btnSm }} disabled={page <= 1} onClick={() => {
                            const next = page - 1;
                            setPage(next);
                            fetchConfig(secret, next, search, userId, sortBy, sortDir);
                        }}>Previous</button>
                        <span style={{ padding: 6 }}>Page {page}</span>
                        <button type="button" style={{ ...styles.btn, ...styles.btnSm }} disabled={page * groups.page_size >= groups.total} onClick={() => {
                            const next = page + 1;
                            setPage(next);
                            fetchConfig(secret, next, search, userId, sortBy, sortDir);
                        }}>Next</button>
                    </div>
                </div>
            </div>
            <EventsPanel headers={headers} apiUrl={apiUrl} styles={styles} />
        </>
    );
}

// ─── InstagramAccountsPanel ───────────────────────────────────────────────────

// Public Instagram accounts the watcher polls daily at noon New York time. Each is a
// synthetic group; the collection checkboxes subscribe that collection's calendar to it,
// exactly as for a WhatsApp group. Adding and running load Instagram in a worker's
// browser, so both come back as a task id this polls until the run finishes.
const IG_TASK_POLL_MS = 3000;
const IG_TASK_MAX_POLLS = 100;

function InstagramAccountsPanel({ apiUrl, headers, styles, collections }) {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [postUrl, setPostUrl] = useState('');
    const [collectionKey, setCollectionKey] = useState('');
    const [adding, setAdding] = useState(false);
    const [running, setRunning] = useState({});  // username -> bool
    const [saving, setSaving] = useState({});    // `${group_id}::${key}` -> bool
    const feedCollections = (collections || []).filter((c) => c.calendar_id);
    const base = `${apiUrl}/admin/apps/jcal/instagram-accounts`;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(base, { headers, timeout: 15000 });
            setAccounts(res.data.accounts || []);
        } catch (e) {
            setError(e.response?.data?.detail || 'Could not load Instagram accounts');
        } finally {
            setLoading(false);
        }
    }, [base, headers]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!collectionKey && feedCollections.length) setCollectionKey(feedCollections[0].key);
    }, [collectionKey, feedCollections]);

    const waitForTask = async (taskId) => {
        for (let i = 0; i < IG_TASK_MAX_POLLS; i += 1) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, IG_TASK_POLL_MS));
            // eslint-disable-next-line no-await-in-loop
            const res = await axios.get(`${base}/tasks/${taskId}`, { headers, timeout: 15000 });
            if (res.data.result) return res.data.result;
        }
        return { status: 'failed', reason: 'still running after 5 minutes; refresh later to see the outcome' };
    };

    const describeRun = (run) => {
        if (!run) return '';
        const published = run.published?.length ?? 0;
        if (run.status === 'ok') return `${published} new post${published !== 1 ? 's' : ''} sent.`;
        return `run failed: ${run.reason || 'unknown reason'}`;
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        setAdding(true);
        setError('');
        setSuccess('');
        try {
            const res = await axios.post(base, { post_url: postUrl, collection_key: collectionKey }, { headers, timeout: 15000 });
            setSuccess('Loading the post and running the account once…');
            const result = await waitForTask(res.data.task_id);
            if (result.status === 'ok') {
                setSuccess(`Added @${result.username}: ${describeRun(result.run)}`);
                setPostUrl('');
            } else {
                setSuccess('');
                setError(`Could not add the account: ${result.reason}`);
            }
            await load();
        } catch (err) {
            setSuccess('');
            setError(err.response?.data?.detail || 'Could not add the account');
        } finally {
            setAdding(false);
        }
    };

    const handleRun = async (account) => {
        setRunning((r) => ({ ...r, [account.username]: true }));
        setError('');
        setSuccess('');
        try {
            const res = await axios.post(`${base}/${encodeURIComponent(account.username)}/run`, {}, { headers, timeout: 15000 });
            const result = await waitForTask(res.data.task_id);
            (result.status === 'ok' ? setSuccess : setError)(`@${account.username}: ${describeRun(result)}`);
            await load();
        } catch (err) {
            setError(err.response?.data?.detail || 'Run failed to start');
        } finally {
            setRunning((r) => ({ ...r, [account.username]: false }));
        }
    };

    const handleToggle = async (account, key, checked) => {
        const linked = account.calendar_event_counts?.[key] ?? 0;
        if (!checked && !window.confirm(
            `Stop feeding ${key.toUpperCase()} from @${account.username}? Its ${linked} event${linked !== 1 ? 's' : ''} on that calendar will be removed.`
        )) return;
        const saveKey = `${account.group_id}::${key}`;
        setSaving((s) => ({ ...s, [saveKey]: true }));
        setError('');
        setSuccess('');
        try {
            const url = `${apiUrl}/admin/apps/jcal/collections/${key}/groups/${encodeURIComponent(account.group_id)}`;
            if (checked) await axios.put(url, {}, { headers, timeout: 30000 });
            else await axios.delete(url, { headers, timeout: 30000 });
            await load();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to update the collection');
        } finally {
            setSaving((s) => ({ ...s, [saveKey]: false }));
        }
    };

    const formatWhen = (iso) => (iso ? new Date(iso).toLocaleString() : 'never');

    return (
        <div style={styles.card}>
            <div style={styles.tableHeader}>
                <h2 style={styles.subheading}>Instagram Accounts</h2>
                <button type="button" style={{ ...styles.btn, ...styles.btnSm }} onClick={load} disabled={loading}>
                    {loading ? 'Loading…' : 'Refresh'}
                </button>
            </div>
            <p style={{ color: '#666', marginTop: 0 }}>
                Public accounts checked daily at noon New York time. Each post's slides go through the
                same pipeline as a WhatsApp flyer. An account nobody follows is not checked.
            </p>
            <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <input
                    placeholder="Link to any recent post, e.g. https://www.instagram.com/p/Dd9PX7aEWs1/"
                    value={postUrl}
                    onChange={(e) => setPostUrl(e.target.value)}
                    style={{ ...styles.input, flex: '1 1 320px', minWidth: 0 }}
                    required
                />
                <select value={collectionKey} onChange={(e) => setCollectionKey(e.target.value)} style={styles.select} required>
                    {feedCollections.map((c) => <option key={c.key} value={c.key}>{c.display_name}</option>)}
                </select>
                <button type="submit" style={styles.btn} disabled={adding || !postUrl || !collectionKey}>
                    {adding ? 'Adding…' : 'Add account'}
                </button>
            </form>
            {error && <p style={styles.error}>{error}</p>}
            {success && <p style={styles.success}>{success}</p>}
            {accounts.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Account</th>
                                <th style={styles.th}>Last run</th>
                                <th style={styles.th}>Slides (7 days)</th>
                                {feedCollections.map((c) => (
                                    <th key={c.key} style={{ ...styles.th, textAlign: 'center' }}>{c.key.toUpperCase()}</th>
                                ))}
                                <th style={styles.th} />
                            </tr>
                        </thead>
                        <tbody>
                            {accounts.map((account) => (
                                <tr key={account.group_id}>
                                    <td style={styles.td}>
                                        <a href={`https://www.instagram.com/${account.username}/`} target="_blank" rel="noreferrer">
                                            @{account.username}
                                        </a>
                                        <div style={{ fontSize: 11, color: '#888' }}>
                                            {account.subscribers} follower{account.subscribers !== 1 ? 's' : ''}
                                            {account.subscribers === 0 ? ' · not checked' : ''}
                                        </div>
                                    </td>
                                    <td style={styles.td}>
                                        <div>{formatWhen(account.last_run_at)}</div>
                                        {account.last_run_status === 'ok' && (
                                            <div style={{ fontSize: 12, color: '#188038' }}>
                                                ok · {account.last_run_published ?? 0} new
                                            </div>
                                        )}
                                        {account.last_run_status === 'failed' && (
                                            <div style={{ fontSize: 12, color: '#c5221f', maxWidth: 360 }}>
                                                failed: {account.last_run_reason || 'unknown reason'}
                                            </div>
                                        )}
                                    </td>
                                    <td style={styles.td}>{account.slides_last_7_days}</td>
                                    {feedCollections.map((c) => {
                                        const saveKey = `${account.group_id}::${c.key}`;
                                        const linked = account.calendar_event_counts?.[c.key] ?? 0;
                                        return (
                                            <td key={c.key} style={{ ...styles.td, textAlign: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={account.collections.includes(c.key)}
                                                    disabled={Boolean(saving[saveKey])}
                                                    onChange={(e) => handleToggle(account, c.key, e.target.checked)}
                                                />
                                                {linked > 0 && <div style={{ fontSize: 11, color: '#888' }}>{linked} linked</div>}
                                            </td>
                                        );
                                    })}
                                    <td style={styles.td}>
                                        <button
                                            type="button"
                                            style={{ ...styles.btn, ...styles.btnSm }}
                                            onClick={() => handleRun(account)}
                                            disabled={Boolean(running[account.username]) || account.subscribers === 0}
                                        >
                                            {running[account.username] ? 'Running…' : 'Run now'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ─── CalendarFeedsPanel ───────────────────────────────────────────────────────

// External event calendars (e.g. jewishla.org's WordPress events calendar) read at noon
// Los Angeles time every interval_days. Each is a synthetic group; the collection
// checkboxes subscribe that collection's calendar to it, as for a WhatsApp group. Preview
// reads the feed without sending anything; Run now sends new and changed events from a
// worker and comes back as a task id this polls until the run finishes.
const FEED_TASK_POLL_MS = 3000;
const FEED_TASK_MAX_POLLS = 100;
// Names for the server's feed kinds (src/services/calendar_feeds ADAPTERS); any other
// kind shows as its key.
const FEED_KIND_LABELS = {
    tribe: 'WordPress Events Calendar',
    squarespace_listing: 'Squarespace listing page',
};

const parsePatterns = (text) => text.split('\n').map((p) => p.trim()).filter(Boolean);

function CalendarFeedsPanel({ apiUrl, headers, styles, collections }) {
    const [feeds, setFeeds] = useState([]);
    const [kinds, setKinds] = useState(['tribe']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [form, setForm] = useState({ name: '', url: '', kind: 'tribe', collectionKey: '', patterns: '', windowDays: 31, intervalDays: 7 });
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [adding, setAdding] = useState(false);
    const [running, setRunning] = useState({});        // group_id -> bool
    const [saving, setSaving] = useState({});          // `${group_id}::${key}` -> bool
    const [editingPatterns, setEditingPatterns] = useState({});  // group_id -> text
    const feedCollections = (collections || []).filter((c) => c.calendar_id);
    const base = `${apiUrl}/admin/apps/jcal/calendar-feeds`;
    const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(base, { headers, timeout: 15000 });
            setFeeds(res.data.feeds || []);
            if (res.data.kinds?.length) setKinds(res.data.kinds);
        } catch (e) {
            setError(e.response?.data?.detail || 'Could not load calendar feeds');
        } finally {
            setLoading(false);
        }
    }, [base, headers]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!form.collectionKey && feedCollections.length) {
            setForm((f) => ({ ...f, collectionKey: feedCollections[0].key }));
        }
    }, [form.collectionKey, feedCollections]);

    const waitForTask = async (taskId) => {
        for (let i = 0; i < FEED_TASK_MAX_POLLS; i += 1) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, FEED_TASK_POLL_MS));
            // eslint-disable-next-line no-await-in-loop
            const res = await axios.get(`${base}/tasks/${taskId}`, { headers, timeout: 15000 });
            if (res.data.result) return res.data.result;
        }
        return { status: 'failed', reason: 'still running after 5 minutes; refresh later to see the outcome' };
    };

    const describeRun = (run) => {
        if (!run) return '';
        if (run.status !== 'ok') return `run failed: ${run.reason || 'unknown reason'}`;
        const sent = run.published?.length ?? 0;
        return `${run.listed} listed, ${sent} new or changed sent, ${run.unchanged} unchanged, ${run.excluded?.length ?? 0} excluded.`;
    };

    const handlePreview = async () => {
        setPreviewing(true);
        setError('');
        setSuccess('');
        try {
            const res = await axios.post(`${base}/preview`, {
                url: form.url,
                kind: form.kind,
                window_days: Number(form.windowDays),
                exclude_patterns: parsePatterns(form.patterns),
            }, { headers, timeout: 120000 });
            setPreview(res.data);
        } catch (err) {
            setPreview(null);
            setError(err.response?.data?.detail || 'Could not read the feed');
        } finally {
            setPreviewing(false);
        }
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        setAdding(true);
        setError('');
        setSuccess('');
        try {
            const res = await axios.post(base, {
                name: form.name,
                url: form.url,
                kind: form.kind,
                collection_key: form.collectionKey,
                exclude_patterns: parsePatterns(form.patterns),
                window_days: Number(form.windowDays),
                interval_days: Number(form.intervalDays),
            }, { headers, timeout: 30000 });
            setSuccess('Added; running the feed once…');
            await load();
            const result = await waitForTask(res.data.task_id);
            (result.status === 'ok' ? setSuccess : setError)(`${form.name}: ${describeRun(result)}`);
            setForm((f) => ({ ...f, name: '', url: '', patterns: '' }));
            setPreview(null);
            await load();
        } catch (err) {
            setSuccess('');
            setError(err.response?.data?.detail || 'Could not add the feed');
        } finally {
            setAdding(false);
        }
    };

    const handleRun = async (feed) => {
        setRunning((r) => ({ ...r, [feed.group_id]: true }));
        setError('');
        setSuccess('');
        try {
            const res = await axios.post(`${base}/${encodeURIComponent(feed.group_id)}/run`, {}, { headers, timeout: 15000 });
            const result = await waitForTask(res.data.task_id);
            (result.status === 'ok' ? setSuccess : setError)(`${feed.name}: ${describeRun(result)}`);
            await load();
        } catch (err) {
            setError(err.response?.data?.detail || 'Run failed to start');
        } finally {
            setRunning((r) => ({ ...r, [feed.group_id]: false }));
        }
    };

    const handleSavePatterns = async (feed) => {
        setError('');
        setSuccess('');
        try {
            await axios.patch(`${base}/${encodeURIComponent(feed.group_id)}`, {
                exclude_patterns: parsePatterns(editingPatterns[feed.group_id] ?? ''),
            }, { headers, timeout: 15000 });
            setEditingPatterns((p) => { const next = { ...p }; delete next[feed.group_id]; return next; });
            setSuccess(`${feed.name}: exclude patterns saved; they apply from the next run.`);
            await load();
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not save the exclude patterns');
        }
    };

    const handleToggle = async (feed, key, checked) => {
        const linked = feed.calendar_event_counts?.[key] ?? 0;
        if (!checked && !window.confirm(
            `Stop feeding ${key.toUpperCase()} from ${feed.name}? Its ${linked} event${linked !== 1 ? 's' : ''} on that calendar will be removed.`
        )) return;
        const saveKey = `${feed.group_id}::${key}`;
        setSaving((s) => ({ ...s, [saveKey]: true }));
        setError('');
        setSuccess('');
        try {
            const url = `${apiUrl}/admin/apps/jcal/collections/${key}/groups/${encodeURIComponent(feed.group_id)}`;
            if (checked) await axios.put(url, {}, { headers, timeout: 30000 });
            else await axios.delete(url, { headers, timeout: 30000 });
            await load();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to update the collection');
        } finally {
            setSaving((s) => ({ ...s, [saveKey]: false }));
        }
    };

    const formatWhen = (iso) => (iso ? new Date(iso).toLocaleString() : 'never');
    const previewIncluded = preview ? preview.items.filter((i) => !i.excluded_by).length : 0;

    return (
        <div style={styles.card}>
            <div style={styles.tableHeader}>
                <h2 style={styles.subheading}>Calendar Feeds</h2>
                <button type="button" style={{ ...styles.btn, ...styles.btnSm }} onClick={load} disabled={loading}>
                    {loading ? 'Loading…' : 'Refresh'}
                </button>
            </div>
            <p style={{ color: '#666', marginTop: 0 }}>
                External event calendars, read at noon Los Angeles time every few days. Each upcoming event
                goes through the same pipeline as a WhatsApp post, once, and again only if its title, time or
                venue changes. A feed nobody follows is not read.
            </p>
            <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 12 }}>
                <input placeholder="Name, e.g. Jewish Federation LA" value={form.name} onChange={setField('name')}
                    style={{ ...styles.input, flex: '1 1 200px', minWidth: 0 }} required />
                <input placeholder="Site URL, e.g. https://www.jewishla.org" value={form.url} onChange={setField('url')}
                    style={{ ...styles.input, flex: '1 1 280px', minWidth: 0 }} required />
                <select value={form.kind} onChange={setField('kind')} style={styles.select} title="Source type">
                    {kinds.map((k) => <option key={k} value={k}>{FEED_KIND_LABELS[k] || k}</option>)}
                </select>
                <select value={form.collectionKey} onChange={setField('collectionKey')} style={styles.select} required>
                    {feedCollections.map((c) => <option key={c.key} value={c.key}>{c.display_name}</option>)}
                </select>
                <label style={{ fontSize: 12, color: '#666' }}>
                    Days ahead{' '}
                    <input type="number" min={1} max={120} value={form.windowDays} onChange={setField('windowDays')} style={{ ...styles.input, width: 64 }} />
                </label>
                <label style={{ fontSize: 12, color: '#666' }}>
                    Every{' '}
                    <input type="number" min={1} max={60} value={form.intervalDays} onChange={setField('intervalDays')} style={{ ...styles.input, width: 56 }} />
                    {' '}days
                </label>
                <textarea placeholder={'Exclude titles matching (one regex per line), e.g.\n^HOLD:\nboard meeting'}
                    value={form.patterns} onChange={setField('patterns')} rows={3}
                    style={{ ...styles.input, flex: '1 1 100%', fontFamily: 'monospace', fontSize: 12 }} />
                <button type="button" style={styles.btn} onClick={handlePreview} disabled={previewing || !form.url}>
                    {previewing ? 'Reading…' : 'Preview'}
                </button>
                <button type="submit" style={styles.btn} disabled={adding || !form.name || !form.url || !form.collectionKey}>
                    {adding ? 'Adding…' : 'Add feed'}
                </button>
            </form>
            {error && <p style={styles.error}>{error}</p>}
            {success && <p style={styles.success}>{success}</p>}
            {preview && (
                <div style={{ marginBottom: 16 }}>
                    <p style={{ margin: '4px 0', color: '#444' }}>
                        {preview.items.length} event{preview.items.length !== 1 ? 's' : ''} from {preview.window[0]} to {preview.window[1]};
                        {' '}{previewIncluded} would be sent, {preview.items.length - previewIncluded} excluded.
                    </p>
                    <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Start</th>
                                    <th style={styles.th}>Title</th>
                                    <th style={styles.th}>Venue</th>
                                    <th style={styles.th}>Excluded by</th>
                                </tr>
                            </thead>
                            <tbody>
                                {preview.items.map((item) => (
                                    <tr key={item.external_id} style={item.excluded_by ? { color: '#999' } : undefined}>
                                        <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                                            {item.all_day ? `${item.start.slice(0, 10)} (all day)` : item.start}
                                        </td>
                                        <td style={styles.td}>
                                            {item.url ? <a href={item.url} target="_blank" rel="noreferrer">{item.name}</a> : item.name}
                                        </td>
                                        <td style={styles.td}>{item.venue}</td>
                                        <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12 }}>{item.excluded_by || ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {feeds.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Feed</th>
                                <th style={styles.th}>Last run</th>
                                <th style={styles.th}>Exclude titles matching</th>
                                {feedCollections.map((c) => (
                                    <th key={c.key} style={{ ...styles.th, textAlign: 'center' }}>{c.key.toUpperCase()}</th>
                                ))}
                                <th style={styles.th} />
                            </tr>
                        </thead>
                        <tbody>
                            {feeds.map((feed) => {
                                const editing = editingPatterns[feed.group_id];
                                return (
                                    <tr key={feed.group_id}>
                                        <td style={styles.td}>
                                            <a href={feed.url} target="_blank" rel="noreferrer">{feed.name}</a>
                                            <div style={{ fontSize: 11, color: '#888' }}>
                                                {feed.kind} · next {feed.window_days} days every {feed.interval_days} day{feed.interval_days !== 1 ? 's' : ''}
                                            </div>
                                            <div style={{ fontSize: 11, color: '#888' }}>
                                                {feed.events_sent} event{feed.events_sent !== 1 ? 's' : ''} sent so far
                                                {feed.subscribers === 0 ? ' · not followed, not read' : ''}
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <div>{formatWhen(feed.last_run_at)}</div>
                                            {feed.last_run_status === 'ok' && (
                                                <div style={{ fontSize: 12, color: '#188038' }}>ok · {feed.last_run_published ?? 0} sent</div>
                                            )}
                                            {feed.last_run_status === 'failed' && (
                                                <div style={{ fontSize: 12, color: '#c5221f', maxWidth: 360 }}>
                                                    failed: {feed.last_run_reason || 'unknown reason'}
                                                </div>
                                            )}
                                        </td>
                                        <td style={styles.td}>
                                            {editing === undefined ? (
                                                <div>
                                                    <div style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
                                                        {feed.exclude_patterns.join('\n') || '—'}
                                                    </div>
                                                    <button type="button" style={{ ...styles.btn, ...styles.btnSm, marginTop: 4 }}
                                                        onClick={() => setEditingPatterns((p) => ({ ...p, [feed.group_id]: feed.exclude_patterns.join('\n') }))}>
                                                        Edit
                                                    </button>
                                                </div>
                                            ) : (
                                                <div>
                                                    <textarea rows={4} value={editing}
                                                        onChange={(e) => setEditingPatterns((p) => ({ ...p, [feed.group_id]: e.target.value }))}
                                                        style={{ ...styles.input, fontFamily: 'monospace', fontSize: 12, width: 240 }} />
                                                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                                        <button type="button" style={{ ...styles.btn, ...styles.btnSm }} onClick={() => handleSavePatterns(feed)}>Save</button>
                                                        <button type="button" style={{ ...styles.btn, ...styles.btnSm }}
                                                            onClick={() => setEditingPatterns((p) => { const next = { ...p }; delete next[feed.group_id]; return next; })}>
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </td>
                                        {feedCollections.map((c) => {
                                            const saveKey = `${feed.group_id}::${c.key}`;
                                            const linked = feed.calendar_event_counts?.[c.key] ?? 0;
                                            return (
                                                <td key={c.key} style={{ ...styles.td, textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={feed.collections.includes(c.key)}
                                                        disabled={Boolean(saving[saveKey])}
                                                        onChange={(e) => handleToggle(feed, c.key, e.target.checked)}
                                                    />
                                                    {linked > 0 && <div style={{ fontSize: 11, color: '#888' }}>{linked} linked</div>}
                                                </td>
                                            );
                                        })}
                                        <td style={styles.td}>
                                            <button
                                                type="button"
                                                style={{ ...styles.btn, ...styles.btnSm }}
                                                onClick={() => handleRun(feed)}
                                                disabled={Boolean(running[feed.group_id]) || feed.subscribers === 0}
                                            >
                                                {running[feed.group_id] ? 'Running…' : 'Run now'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ─── CalendarPopover ──────────────────────────────────────────────────────────

function CalendarPopover({ event, headers, apiUrl, onCountChange, onClose }) {
    const [assigned, setAssigned] = useState(null);   // null = loading
    const [searchQ, setSearchQ] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [adding, setAdding] = useState(new Set());
    const [removing, setRemoving] = useState(new Set());
    const [err, setErr] = useState('');
    const searchRef = React.useRef();

    useEffect(() => {
        axios.get(`${apiUrl}/admin/events/${event.id}/calendars`, { headers })
            .then((r) => setAssigned(r.data))
            .catch(() => setErr('Failed to load calendars.'));
    }, [event.id, apiUrl, headers]);

    const doSearch = useCallback(async (q) => {
        setSearching(true);
        try {
            const r = await axios.get(`${apiUrl}/admin/events/calendars/search?q=${encodeURIComponent(q)}&app=jcal`, { headers });
            const assignedIds = new Set((assigned || []).map((c) => c.id));
            setResults(r.data.filter((c) => !assignedIds.has(c.id)));
        } catch {
            setErr('Search failed.');
        } finally {
            setSearching(false);
        }
    }, [apiUrl, headers, assigned]);

    useEffect(() => {
        if (searchQ.length === 0) { setResults([]); return; }
        const t = setTimeout(() => doSearch(searchQ), 300);
        return () => clearTimeout(t);
    }, [searchQ, doSearch]);

    const handleAdd = async (cal) => {
        setAdding((p) => new Set(p).add(cal.id)); setErr('');
        try {
            await axios.post(`${apiUrl}/admin/events/${event.id}/calendars/${cal.id}`, {}, { headers });
            setAssigned((prev) => [...(prev || []), cal].sort((a, b) => a.name.localeCompare(b.name)));
            setResults((prev) => prev.filter((c) => c.id !== cal.id));
            onCountChange(event.id, (assigned?.length ?? 0) + 1);
        } catch (e) {
            setErr(e.response?.data?.detail || 'Add failed.');
        } finally {
            setAdding((p) => { const n = new Set(p); n.delete(cal.id); return n; });
        }
    };

    const handleRemove = async (cal) => {
        setRemoving((p) => new Set(p).add(cal.id)); setErr('');
        try {
            await axios.delete(`${apiUrl}/admin/events/${event.id}/calendars/${cal.id}`, { headers });
            setAssigned((prev) => prev.filter((c) => c.id !== cal.id));
            onCountChange(event.id, (assigned?.length ?? 1) - 1);
        } catch (e) {
            setErr(e.response?.data?.detail || 'Remove failed.');
        } finally {
            setRemoving((p) => { const n = new Set(p); n.delete(cal.id); return n; });
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
            <div style={{ background: '#fff', borderRadius: 8, padding: 24, width: 480, maxWidth: '95vw', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: 16 }}>Calendars</h3>
                        <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{event.title}</div>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#666', lineHeight: 1 }}>×</button>
                </div>

                {err && <p style={{ color: '#d93025', fontSize: 13, margin: '0 0 10px' }}>{err}</p>}

                {/* Assigned calendars */}
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Assigned</div>
                    {assigned === null ? (
                        <p style={{ color: '#888', fontSize: 13 }}>Loading…</p>
                    ) : assigned.length === 0 ? (
                        <p style={{ color: '#aaa', fontSize: 13 }}>Not assigned to any calendars.</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {assigned.map((cal) => (
                                <div key={cal.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: '#f8f9fa', borderRadius: 6, border: '1px solid #e0e0e0' }}>
                                    <div>
                                        <div style={{ fontSize: 13, fontWeight: 500 }}>{cal.name}</div>
                                        <div style={{ fontSize: 11, color: '#999', fontFamily: 'monospace' }}>{cal.id}</div>
                                    </div>
                                    <button
                                        onClick={() => handleRemove(cal)}
                                        disabled={removing.has(cal.id)}
                                        style={{ fontSize: 12, padding: '3px 10px', background: '#fff', color: '#d93025', border: '1px solid #d93025', borderRadius: 4, cursor: 'pointer' }}
                                    >
                                        {removing.has(cal.id) ? '…' : 'Remove'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Search & add */}
                <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Add to calendar</div>
                    <input
                        ref={searchRef}
                        placeholder="Search by name…"
                        value={searchQ}
                        onChange={(e) => setSearchQ(e.target.value)}
                        style={{ width: '100%', padding: '7px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }}
                        autoFocus
                    />
                    {searching && <p style={{ color: '#888', fontSize: 12, margin: '6px 0 0' }}>Searching…</p>}
                    {results.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                            {results.map((cal) => (
                                <div key={cal.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: '#f0f7ff', borderRadius: 6, border: '1px solid #c5d8fb' }}>
                                    <div>
                                        <div style={{ fontSize: 13, fontWeight: 500 }}>{cal.name}</div>
                                        <div style={{ fontSize: 11, color: '#999', fontFamily: 'monospace' }}>{cal.id}</div>
                                    </div>
                                    <button
                                        onClick={() => handleAdd(cal)}
                                        disabled={adding.has(cal.id)}
                                        style={{ fontSize: 12, padding: '3px 10px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                                    >
                                        {adding.has(cal.id) ? '…' : 'Add'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    {!searching && searchQ.length > 0 && results.length === 0 && (
                        <p style={{ color: '#aaa', fontSize: 12, margin: '6px 0 0' }}>No unassigned calendars match.</p>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── LocationAutocomplete ─────────────────────────────────────────────────────

function LocationAutocomplete({ value, onChange, headers, apiUrl, inputStyle }) {
    const [query, setQuery] = useState(value || '');
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [searchErr, setSearchErr] = useState('');
    const debounceRef = useRef(null);

    useEffect(() => { setQuery(value || ''); }, [value]);

    const search = async (q) => {
        if (!q || q.length < 2) { setSuggestions([]); setOpen(false); setSearchErr(''); return; }
        setSearchErr('');
        try {
            const res = await axios.get(`${apiUrl}/admin/events/geocode-search`, { params: { q }, headers });
            const data = res.data || [];
            setSuggestions(data);
            setOpen(data.length > 0);
            if (data.length === 0) setSearchErr('No results');
        } catch (err) {
            setSuggestions([]); setOpen(false);
            setSearchErr(err.response?.data?.detail || `Search error ${err.response?.status || ''}`);
        }
    };

    const handleChange = (e) => {
        const q = e.target.value;
        setQuery(q);
        onChange({ location_address: q });
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => search(q), 300);
    };

    const handleSelect = (s) => {
        setQuery(s.location_address || s.label || '');
        setSuggestions([]); setOpen(false); setSearchErr('');
        onChange({
            location_address: s.location_address || s.label || '',
            location_city_display: s.location_city_display || '',
            location_city_key: s.location_city_key || '',
            location_neighborhood: s.location_neighborhood || '',
            latitude: s.latitude,
            longitude: s.longitude,
        });
    };

    return (
        <div style={{ position: 'relative' }}>
            <input
                type="text"
                value={query}
                onChange={handleChange}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                onFocus={() => suggestions.length > 0 && setOpen(true)}
                style={inputStyle}
                placeholder="Type to search…"
            />
            {searchErr && <div style={{ fontSize: 11, color: '#c00', marginTop: 2 }}>{searchErr}</div>}
            {open && suggestions.length > 0 && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 2000,
                    background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 220, overflowY: 'auto',
                }}>
                    {suggestions.map((s, i) => (
                        <div
                            key={i}
                            onMouseDown={() => handleSelect(s)}
                            style={{
                                padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                                borderBottom: i < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                                background: '#fff',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f0f7ff'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                        >
                            {s.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── EventsPanel ──────────────────────────────────────────────────────────────

const CITY_KEY_OPTIONS = [
    { value: '', label: '— none —' },
    { value: 'los_angeles', label: 'Los Angeles' },
    { value: 'new_york_city', label: 'New York City' },
    { value: 'orange_county', label: 'Orange County' },
];

const EDITABLE_FIELDS = [
    { key: 'title', label: 'Title', type: 'text' },
    { key: 'start_time', label: 'Start time', type: 'datetime-local' },
    { key: 'end_time', label: 'End time', type: 'datetime-local' },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'event_image_url', label: 'Image URL', type: 'text' },
    { key: 'location_name', label: 'Location name', type: 'text' },
    { key: 'location_address', label: 'Location address', type: 'geocode' },
    { key: 'location_city_display', label: 'City display', type: 'text' },
    { key: 'location_city_key', label: 'City calendar', type: 'select', options: CITY_KEY_OPTIONS },
    { key: 'location_neighborhood', label: 'Neighborhood', type: 'text' },
    { key: 'latitude', label: 'Latitude', type: 'number' },
    { key: 'longitude', label: 'Longitude', type: 'number' },
    { key: 'tags', label: 'Tags (comma-separated)', type: 'tags' },
];

function toLocalDatetimeValue(isoStr) {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    if (isNaN(d)) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EventsPanel({ headers, apiUrl, styles }) {
    const [events, setEvents] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 50;

    const [search, setSearch] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortDir, setSortDir] = useState('desc');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [selected, setSelected] = useState(new Set());
    const [editingEvent, setEditingEvent] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);
    const [deleting, setDeleting] = useState(new Set());
    const [purging, setPurging] = useState(new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [calendarPopoverEvent, setCalendarPopoverEvent] = useState(null);

    const fetchEvents = useCallback(async (pg = page, s = search, sf = sortBy, sd = sortDir, df = dateFrom, dt = dateTo) => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                page: pg, page_size: PAGE_SIZE, sort_by: sf, sort_dir: sd,
            });
            if (s) params.set('search', s);
            if (df) params.set('date_from', df);
            if (dt) params.set('date_to', dt);
            const res = await axios.get(`${apiUrl}/admin/events?${params}`, { headers });
            setEvents(res.data.events);
            setTotal(res.data.total);
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed to load events.');
        } finally {
            setLoading(false);
        }
    }, [page, search, sortBy, sortDir, dateFrom, dateTo, apiUrl, headers]);

    useEffect(() => { fetchEvents(1, search, sortBy, sortDir, dateFrom, dateTo); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSort = (col) => {
        const newDir = sortBy === col ? (sortDir === 'asc' ? 'desc' : 'asc') : 'desc';
        setSortBy(col); setSortDir(newDir); setPage(1);
        fetchEvents(1, search, col, newDir, dateFrom, dateTo);
    };

    const handleSearch = () => { setPage(1); fetchEvents(1, search, sortBy, sortDir, dateFrom, dateTo); };

    const sortIcon = (col) => sortBy !== col ? ' ↕' : sortDir === 'asc' ? ' ↑' : ' ↓';

    const toggleSelect = (id) => setSelected((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const toggleAll = () => setSelected((prev) => prev.size === events.length ? new Set() : new Set(events.map((e) => e.id)));

    const openEdit = (ev) => {
        setEditingEvent(ev);
        setEditForm({
            ...ev,
            start_time: toLocalDatetimeValue(ev.start_time),
            end_time: toLocalDatetimeValue(ev.end_time),
            tags: (ev.tags || []).join(', '),
        });
    };
    const closeEdit = () => {
        setEditingEvent(null);
        setEditForm({});
        setImageUploading(false);
    };

    const handleImageUpload = async (file) => {
        if (!file || !editingEvent) return;
        setImageUploading(true); setError(''); setSuccess('');
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await axios.post(
                `${apiUrl}/admin/events/${editingEvent.id}/image`,
                formData,
                { headers }
            );
            setEvents((prev) => prev.map((e) => e.id === res.data.id ? res.data : e));
            setEditingEvent(res.data);
            setEditForm((prev) => ({
                ...prev,
                event_image_url: res.data.event_image_url || '',
                resized_image_url: res.data.resized_image_url || '',
            }));
            setSuccess(`Image uploaded for "${res.data.title}".`);
        } catch (e) {
            setError(e.response?.data?.detail || 'Image upload failed.');
        } finally {
            setImageUploading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true); setError(''); setSuccess('');
        try {
            const payload = {};
            EDITABLE_FIELDS.forEach(({ key, type }) => {
                if (type === 'tags') {
                    payload[key] = editForm[key] ? editForm[key].split(',').map((t) => t.trim()).filter(Boolean) : [];
                } else if (type === 'datetime-local') {
                    payload[key] = editForm[key] ? new Date(editForm[key]).toISOString() : null;
                } else if (type === 'number') {
                    const v = parseFloat(editForm[key]);
                    payload[key] = isNaN(v) ? null : v;
                } else {
                    payload[key] = editForm[key] || null;
                }
            });
            const res = await axios.patch(`${apiUrl}/admin/events/${editingEvent.id}`, payload, { headers });
            setEvents((prev) => prev.map((e) => e.id === res.data.id ? res.data : e));
            setSuccess(`"${res.data.title}" saved.`);
            closeEdit();
        } catch (e) {
            setError(e.response?.data?.detail || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (ev, purgeVisitedPage = false) => {
        if (!window.confirm(`Delete "${ev.title}"?${purgeVisitedPage ? '\n\nThis will also purge the visited_page entry.' : ''}`)) return;
        setDeleting((p) => new Set(p).add(ev.id)); setError(''); setSuccess('');
        try {
            await axios.delete(`${apiUrl}/admin/events/${ev.id}?purge_visited_page=${purgeVisitedPage}`, { headers });
            setEvents((prev) => prev.filter((e) => e.id !== ev.id));
            setTotal((t) => t - 1);
            setSelected((p) => { const n = new Set(p); n.delete(ev.id); return n; });
            setSuccess(`Deleted "${ev.title}".`);
        } catch (e) {
            setError(e.response?.data?.detail || 'Delete failed.');
        } finally {
            setDeleting((p) => { const n = new Set(p); n.delete(ev.id); return n; });
        }
    };

    const handleBulkDelete = async (purgeVisitedPages = false) => {
        const ids = [...selected];
        if (!ids.length) return;
        if (!window.confirm(`Delete ${ids.length} event(s)?${purgeVisitedPages ? '\n\nThis will also purge their visited_page entries.' : ''}`)) return;
        setBulkDeleting(true); setError(''); setSuccess('');
        try {
            const res = await axios.post(
                `${apiUrl}/admin/events/bulk-delete?purge_visited_pages=${purgeVisitedPages}`,
                { ids }, { headers }
            );
            setEvents((prev) => prev.filter((e) => !selected.has(e.id)));
            setTotal((t) => t - res.data.deleted);
            setSelected(new Set());
            setSuccess(`Deleted ${res.data.deleted} event(s).${res.data.purged_visited_pages ? ` Purged ${res.data.purged_visited_pages} visited page(s).` : ''}`);
        } catch (e) {
            setError(e.response?.data?.detail || 'Bulk delete failed.');
        } finally {
            setBulkDeleting(false);
        }
    };

    const handleCalendarCountChange = (eventId, newCount) => {
        setEvents((prev) => prev.map((e) => e.id === eventId ? { ...e, calendar_count: newCount } : e));
    };

    const handlePurge = async (ev) => {
        setPurging((p) => new Set(p).add(ev.id)); setError(''); setSuccess('');
        try {
            const res = await axios.post(`${apiUrl}/admin/events/${ev.id}/purge-visited-page`, {}, { headers });
            setSuccess(`Purged visited page: ${res.data.purged_url}`);
        } catch (e) {
            setError(e.response?.data?.detail || 'Purge failed.');
        } finally {
            setPurging((p) => { const n = new Set(p); n.delete(ev.id); return n; });
        }
    };

    const thS = { ...styles.th, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

    return (
        <>
            {calendarPopoverEvent && (
                <CalendarPopover
                    event={calendarPopoverEvent}
                    headers={headers}
                    apiUrl={apiUrl}
                    onCountChange={handleCalendarCountChange}
                    onClose={() => setCalendarPopoverEvent(null)}
                />
            )}
            {editingEvent && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: '#fff', borderRadius: 8, padding: 28, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                        <h2 style={{ margin: '0 0 18px', fontSize: 18 }}>Edit Event</h2>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {EDITABLE_FIELDS.map(({ key, label, type, options }) => (
                                <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    <label style={styles.label}>{label}</label>
                                    {key === 'event_image_url' ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {(editForm.resized_image_url || editForm.event_image_url) && (
                                                <img
                                                    src={editForm.resized_image_url || editForm.event_image_url}
                                                    alt=""
                                                    style={{ width: 220, maxWidth: '100%', height: 'auto', border: '1px solid #e0e0e0', borderRadius: 4, background: '#f8f8f8' }}
                                                />
                                            )}
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp"
                                                disabled={imageUploading}
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    e.target.value = '';
                                                    handleImageUpload(file);
                                                }}
                                                style={styles.input}
                                            />
                                            <input
                                                type="text"
                                                value={editForm[key] || ''}
                                                onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                                                style={styles.input}
                                                placeholder="Image URL"
                                            />
                                            {imageUploading && <span style={{ fontSize: 12, color: '#888' }}>Uploading image…</span>}
                                        </div>
                                    ) : type === 'textarea' ? (
                                        <textarea
                                            value={editForm[key] || ''}
                                            onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                                            rows={4}
                                            style={{ ...styles.input, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
                                        />
                                    ) : type === 'datetime-local' ? (
                                        <input
                                            type="datetime-local"
                                            value={editForm[key] || ''}
                                            onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                                            style={styles.input}
                                        />
                                    ) : type === 'select' ? (
                                        <select
                                            value={editForm[key] || ''}
                                            onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                                            style={{ ...styles.input, fontSize: 13 }}
                                        >
                                            {options.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    ) : type === 'geocode' ? (
                                        <LocationAutocomplete
                                            value={editForm[key] || ''}
                                            onChange={(updates) => setEditForm(prev => ({ ...prev, ...updates }))}
                                            headers={headers}
                                            apiUrl={apiUrl}
                                            inputStyle={styles.input}
                                        />
                                    ) : type === 'number' ? (
                                        <input
                                            type="number"
                                            step="any"
                                            value={editForm[key] ?? ''}
                                            onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                                            style={styles.input}
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            value={editForm[key] || ''}
                                            onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                                            style={styles.input}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                        {error && <p style={styles.error}>{error}</p>}
                        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                            <button onClick={handleSave} disabled={saving} style={styles.btn}>{saving ? 'Saving…' : 'Save'}</button>
                            <button onClick={closeEdit} style={{ ...styles.btn, ...styles.btnSecondary }}>Cancel</button>
                            <button
                                type="button"
                                style={{ ...styles.btn, background: '#555', marginLeft: 'auto' }}
                                onClick={() => setCalendarPopoverEvent(editingEvent)}
                            >
                                Calendars
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div style={styles.card}>
                <div style={styles.tableHeader}>
                    <h2 style={styles.subheading}>Events</h2>
                    <button type="button" style={{ ...styles.btn, ...styles.btnSm }} onClick={() => fetchEvents(page, search, sortBy, sortDir, dateFrom, dateTo)} disabled={loading}>
                        Refresh
                    </button>
                </div>

                {error && <p style={styles.error}>{error}</p>}
                {success && <p style={styles.success}>{success}</p>}

                {/* Filters */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'flex-end' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <label style={{ fontSize: 11, color: '#666' }}>Search</label>
                        <input
                            placeholder="Title or group…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            style={{ ...styles.input, width: 220 }}
                        />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <label style={{ fontSize: 11, color: '#666' }}>Start from</label>
                        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.input} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <label style={{ fontSize: 11, color: '#666' }}>Start to</label>
                        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.input} />
                    </div>
                    <button type="button" style={styles.btn} onClick={handleSearch}>Search</button>
                </div>

                {/* Bulk actions */}
                {selected.size > 0 && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', padding: '8px 12px', background: '#e8f0fe', borderRadius: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{selected.size} selected</span>
                        <button
                            type="button"
                            style={{ ...styles.btn, ...styles.btnSm, ...styles.btnDanger }}
                            onClick={() => handleBulkDelete(false)}
                            disabled={bulkDeleting}
                        >
                            {bulkDeleting ? 'Deleting…' : 'Delete selected'}
                        </button>
                        <button
                            type="button"
                            style={{ ...styles.btn, ...styles.btnSm, ...styles.btnDanger }}
                            onClick={() => handleBulkDelete(true)}
                            disabled={bulkDeleting}
                        >
                            {bulkDeleting ? 'Deleting…' : 'Delete + purge pages'}
                        </button>
                        <button type="button" style={{ ...styles.btn, ...styles.btnSm, ...styles.btnSecondary }} onClick={() => setSelected(new Set())}>
                            Clear
                        </button>
                    </div>
                )}

                {loading ? (
                    <p style={{ color: '#888' }}>Loading…</p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>
                                        <input
                                            type="checkbox"
                                            checked={events.length > 0 && selected.size === events.length}
                                            onChange={toggleAll}
                                        />
                                    </th>
                                    <th style={thS} onClick={() => handleSort('title')}>Title{sortIcon('title')}</th>
                                    <th style={thS} onClick={() => handleSort('start_time')}>Start{sortIcon('start_time')}</th>
                                    <th style={styles.th}>Source URL</th>
                                    <th style={styles.th}>Group</th>
                                    <th style={styles.th}>Location</th>
                                    <th style={thS} onClick={() => handleSort('created_at')}>Created{sortIcon('created_at')}</th>
                                    <th style={styles.th}>Cals</th>
                                    <th style={styles.th}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.length === 0 ? (
                                    <tr><td colSpan={9} style={{ ...styles.td, color: '#888', textAlign: 'center' }}>No events found.</td></tr>
                                ) : events.map((ev) => (
                                    <tr key={ev.id} style={selected.has(ev.id) ? styles.activeRow : {}}>
                                        <td style={styles.td}>
                                            <input type="checkbox" checked={selected.has(ev.id)} onChange={() => toggleSelect(ev.id)} />
                                        </td>
                                        <td style={{ ...styles.td, maxWidth: 240 }}>
                                            <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ev.title}>{ev.title}</div>
                                            {ev.tags?.length > 0 && (
                                                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{ev.tags.join(', ')}</div>
                                            )}
                                        </td>
                                        <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                                            {ev.start_time ? new Date(ev.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit' }) : '—'}
                                        </td>
                                        <td style={{ ...styles.td, maxWidth: 200 }}>
                                            {ev.source_url ? (
                                                <a href={ev.source_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#1a73e8', fontFamily: 'monospace', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ev.source_url}>
                                                    {ev.source_url.replace(/^https?:\/\//, '')}
                                                </a>
                                            ) : '—'}
                                        </td>
                                        <td style={{ ...styles.td, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ev.group_name}>
                                            {ev.group_name || ev.group_id || '—'}
                                        </td>
                                        <td style={{ ...styles.td, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ev.location_name}>
                                            {ev.location_name || ev.location_city_display || '—'}
                                        </td>
                                        <td style={{ ...styles.td, whiteSpace: 'nowrap', color: '#888' }}>
                                            {ev.created_at ? new Date(ev.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>
                                            <button
                                                type="button"
                                                onClick={() => setCalendarPopoverEvent(ev)}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, color: ev.calendar_count > 0 ? '#1a73e8' : '#aaa', padding: '2px 6px', borderRadius: 4, textDecoration: ev.calendar_count > 0 ? 'underline' : 'none' }}
                                                title="Manage calendar assignments"
                                            >
                                                {ev.calendar_count}
                                            </button>
                                        </td>
                                        <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                                            <div style={{ display: 'flex', gap: 4 }}>
                                                <button
                                                    type="button"
                                                    style={{ ...styles.btn, ...styles.btnSm, fontSize: 12, padding: '3px 8px' }}
                                                    onClick={() => openEdit(ev)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    type="button"
                                                    style={{ ...styles.btn, ...styles.btnSm, ...styles.btnDanger, fontSize: 12, padding: '3px 8px' }}
                                                    onClick={() => handleDelete(ev)}
                                                    disabled={deleting.has(ev.id)}
                                                >
                                                    {deleting.has(ev.id) ? '…' : 'Del'}
                                                </button>
                                                {ev.source_url && (
                                                    <button
                                                        type="button"
                                                        style={{ ...styles.btn, ...styles.btnSm, fontSize: 12, padding: '3px 8px', background: '#f57c00' }}
                                                        onClick={() => handlePurge(ev)}
                                                        disabled={purging.has(ev.id)}
                                                        title="Purge visited_pages entry for this event's source URL"
                                                    >
                                                        {purging.has(ev.id) ? '…' : 'Purge'}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
                    <button
                        type="button"
                        style={{ ...styles.btn, ...styles.btnSm }}
                        disabled={page <= 1}
                        onClick={() => { const p = page - 1; setPage(p); fetchEvents(p, search, sortBy, sortDir, dateFrom, dateTo); }}
                    >
                        Previous
                    </button>
                    <span style={{ fontSize: 13, color: '#555' }}>Page {page} · {total} total</span>
                    <button
                        type="button"
                        style={{ ...styles.btn, ...styles.btnSm }}
                        disabled={page * PAGE_SIZE >= total}
                        onClick={() => { const p = page + 1; setPage(p); fetchEvents(p, search, sortBy, sortDir, dateFrom, dateTo); }}
                    >
                        Next
                    </button>
                </div>
            </div>
        </>
    );
}

// ──────────────────────────────────────────────────────────────────────────────

const collectionBtnStyle = {
    fontSize: 11, fontWeight: 600, padding: '2px 8px',
    border: '1px solid', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap',
};

const TERMINAL_JOB_STATUSES = new Set(['completed', 'completed_with_errors', 'cancelled', 'error']);

function normalizeSyncJob(raw) {
    if (!raw) return null;
    const total = Number(raw.total_messages ?? raw.total ?? 0);
    const completed = Number(raw.completed_messages ?? raw.completed ?? 0);
    const pct = raw.percent_complete != null
        ? Number(raw.percent_complete)
        : (total > 0 ? (completed / total) * 100 : 0);
    return {
        ...raw,
        job_id: raw.job_id,
        status: raw.status || 'unknown',
        total_messages: total,
        completed_messages: completed,
        percent_complete: Math.min(Math.max(Number.isFinite(pct) ? pct : 0, 0), 100),
    };
}

function SyncPanel({ group, collectionKey, syncStats, setSyncStats, syncSinceDays, setSyncSinceDays, launching, onLaunch, onClose, activeJobs, setActiveJobs, onDismissJob, headers, apiUrl }) {
    const key = `${group.group_id}::${collectionKey}`;
    const stats = syncStats[key] || { loading: true, available: null };
    const sinceDays = syncSinceDays[key] ?? 30;
    const available = stats.available ?? 0;
    const linkedCount = group.calendar_event_counts?.[collectionKey] ?? 0;
    const isLaunching = Boolean(launching[key]);
    const canLaunch = !stats.loading && !stats.error && available > 0 && !isLaunching;
    const job = normalizeSyncJob(activeJobs[key]);
    const jobId = job?.job_id;
    const jobStatus = job?.status;

    React.useEffect(() => {
        if (!jobId || TERMINAL_JOB_STATUSES.has(jobStatus)) return;
        let cancelled = false;
        const pollJob = async () => {
            try {
                const res = await axios.get(`${apiUrl}/admin/apps/sync-jobs/${jobId}`, { headers });
                if (cancelled) return;
                setActiveJobs((prev) => ({ ...prev, [key]: normalizeSyncJob(res.data) }));
            } catch (err) {
                if (cancelled) return;
                setActiveJobs((prev) => ({
                    ...prev,
                    [key]: {
                        ...normalizeSyncJob(prev[key]),
                        poll_error: err.response?.data?.detail || err.message || 'Failed to refresh sync status',
                    },
                }));
            }
        };
        pollJob();
        const timer = setInterval(pollJob, 2500);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [jobId, jobStatus, key, apiUrl, headers, setActiveJobs]);

    const handleSinceChange = async (e) => {
        const nextSinceDays = Number(e.target.value);
        setSyncSinceDays((prev) => ({ ...prev, [key]: nextSinceDays }));
        setSyncStats((prev) => ({ ...prev, [key]: { loading: true, available: null } }));
        try {
            const res = await axios.get(
                `${apiUrl}/admin/apps/jcal/collections/${collectionKey}/groups/${encodeURIComponent(group.group_id)}/sync-stats`,
                { headers, params: { since_days: nextSinceDays }, timeout: 15000 }
            );
            setSyncStats((prev) => ({ ...prev, [key]: { loading: false, available: res.data.available } }));
        } catch {
            setSyncStats((prev) => ({ ...prev, [key]: { loading: false, available: null, error: true } }));
        }
    };

    const panelStyle = { background: '#f0f6ff', borderTop: '1px solid #c5d8fb', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 };
    const title = <span style={{ fontSize: 13, fontWeight: 600, color: '#1a73e8' }}>Sync · {group.group_name || group.group_id} → {collectionKey.toUpperCase()}</span>;

    if (job) {
        const total = job.total_messages ?? 0;
        const completed = job.completed_messages ?? 0;
        const pct = Math.round(job.percent_complete ?? (total > 0 ? (completed / total) * 100 : 0));
        const isTerminal = TERMINAL_JOB_STATUSES.has(job.status);
        const isError = job.status === 'error' || job.status === 'completed_with_errors';
        const isDone = job.status === 'completed';

        return (
            <div style={panelStyle}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    {title}
                    <span style={{ fontSize: 12, color: isError ? '#c5221f' : isDone ? '#188038' : '#888', display: 'flex', alignItems: 'center', gap: 5 }}>
                        {!isDone && !isError && (
                            <span style={{
                                display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
                                background: '#1a73e8', animation: 'syncPulse 1.2s ease-in-out infinite',
                            }} />
                        )}
                        {isDone ? 'Complete' : isError ? job.status.replace(/_/g, ' ') : 'Running…'}
                    </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#555' }}>
                        <span>{completed} / {total} messages</span>
                        <span>{pct}%</span>
                    </div>
                    <div style={{ background: '#dde8fb', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                        <div style={{
                            height: '100%',
                            width: `${pct}%`,
                            borderRadius: 4,
                            background: isError ? '#c5221f' : isDone ? '#188038' : '#1a73e8',
                            transition: 'width 0.4s ease',
                        }} />
                    </div>
                    {job.poll_error && !isTerminal && (
                        <span style={{ fontSize: 11, color: '#c5221f' }}>
                            Status refresh failed: {job.poll_error}
                        </span>
                    )}
                    <span style={{ fontSize: 11, color: '#888', fontFamily: 'monospace' }}>job {job.job_id}</span>
                </div>
                {isTerminal && (
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => onDismissJob(group, collectionKey)} style={{ fontSize: 13, padding: '6px 14px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                            Done
                        </button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div style={panelStyle}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                {title}
                {stats.loading && <span style={{ fontSize: 12, color: '#888' }}>Checking available messages…</span>}
            </div>
            <div style={{ display: 'flex', gap: 32 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Available to sync</span>
                    {stats.loading ? (
                        <span style={{ fontSize: 22, fontWeight: 700, color: '#aaa' }}>—</span>
                    ) : stats.error ? (
                        <span style={{ fontSize: 13, color: '#c5221f' }}>Failed to load</span>
                    ) : (
                        <span style={{ fontSize: 22, fontWeight: 700, color: available > 0 ? '#1a73e8' : '#aaa' }}>{available}</span>
                    )}
                    <span style={{ fontSize: 11, color: '#888' }}>unsynced messages</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Already linked</span>
                    <span style={{ fontSize: 18, fontWeight: 600, color: '#555' }}>{linkedCount}</span>
                    <span style={{ fontSize: 11, color: '#888' }}>CalendarEvent records</span>
                </div>
            </div>
            {!stats.loading && !stats.error && available > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, borderTop: '1px solid #c5d8fb', paddingTop: 12 }}>
                    <label style={{ fontSize: 12, color: '#555', fontWeight: 500 }}>Since</label>
                    <select
                        value={sinceDays}
                        onChange={handleSinceChange}
                        style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #c5d8fb', borderRadius: 4, background: '#fff' }}
                    >
                        <option value={30}>Last 30 days</option>
                        <option value={60}>Last 60 days</option>
                        <option value={90}>Last 90 days</option>
                    </select>
                </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                    onClick={() => onLaunch(group, collectionKey)}
                    disabled={!canLaunch}
                    style={{ fontSize: 13, fontWeight: 600, padding: '7px 18px', background: canLaunch ? '#1a73e8' : '#a8c7fa', color: '#fff', border: 'none', borderRadius: 4, cursor: canLaunch ? 'pointer' : 'not-allowed' }}
                >
                    {isLaunching ? 'Launching…' : 'Launch Sync'}
                </button>
                <button onClick={onClose} style={{ fontSize: 13, padding: '7px 14px', background: '#fff', color: '#555', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>
                    Cancel
                </button>
                {!stats.loading && !stats.error && available === 0 && (
                    <span style={{ fontSize: 11, color: '#888' }}>Nothing to sync within the retention window.</span>
                )}
                {!stats.loading && !stats.error && available > 0 && (
                    <span style={{ fontSize: 11, color: '#888' }}>Queues {available} message{available !== 1 ? 's' : ''} through the standard sync pipeline.</span>
                )}
            </div>
        </div>
    );
}

function shortGroup(group) {
    if (!group) return '—';
    if (group.includes('ios-beta-testers')) return 'iOS Tester';
    if (group.includes('ios-beta-waitlist')) return 'iOS Waitlist';
    if (group.includes('android-beta-testers')) return 'Android Tester';
    if (group.includes('android-beta-waitlist')) return 'Android Waitlist';
    return group;
}

function groupBadgeStyle(group) {
    const base = { padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600, display: 'inline-block' };
    if (!group) return { ...base, background: '#f0f0f0', color: '#999' };
    if (group.includes('testers')) return { ...base, background: '#e6f4ea', color: '#188038' };
    if (group.includes('waitlist')) return { ...base, background: '#fef7e0', color: '#b06000' };
    return { ...base, background: '#f0f0f0', color: '#555' };
}

const styles = {
    page: { fontFamily: 'sans-serif', maxWidth: 1100, margin: '0 auto', padding: '24px 16px', color: '#222' },
    topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
    card: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 24, marginBottom: 24 },
    tabs: { display: 'flex', gap: 8, marginBottom: 16 },
    tab: { padding: '8px 14px', background: '#fff', color: '#1a73e8', border: '1px solid #c7d7f2', borderRadius: 4, cursor: 'pointer', fontSize: 14 },
    activeTab: { background: '#1a73e8', color: '#fff', border: '1px solid #1a73e8' },
    heading: { margin: '0 0 16px', fontSize: 22 },
    subheading: { margin: '0 0 16px', fontSize: 18 },
    form: { display: 'flex', flexDirection: 'column', gap: 12 },
    row: { display: 'flex', flexDirection: 'column', gap: 4 },
    inlineRow: { display: 'flex', gap: 24, flexWrap: 'wrap' },
    fieldGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
    label: { fontWeight: 600, fontSize: 13 },
    input: { padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 },
    select: { padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 12 },
    formActions: { display: 'flex', gap: 8 },
    btn: { padding: '8px 18px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 14 },
    btnSm: { padding: '5px 12px', fontSize: 13 },
    btnSecondary: { background: '#888' },
    btnDanger: { background: '#d93025' },
    error: { color: '#d93025', margin: '4px 0', fontSize: 13 },
    success: { color: '#188038', margin: '4px 0', fontSize: 13 },
    qrResult: { marginTop: 20, paddingTop: 16, borderTop: '1px solid #e0e0e0' },
    monoLine: { fontFamily: 'monospace', fontSize: 13, margin: '6px 0', overflowWrap: 'anywhere' },
    qrImage: { display: 'block', width: 280, maxWidth: '100%', height: 'auto', marginTop: 12, border: '1px solid #e0e0e0' },
    tableHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e0e0e0', fontSize: 13, fontWeight: 600 },
    td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0', fontSize: 13, verticalAlign: 'middle' },
    activeRow: { background: '#e8f0fe' },
};

const _styleEl = document.createElement('style');
_styleEl.textContent = `@keyframes syncPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.35;transform:scale(0.7)} }`;
document.head.appendChild(_styleEl);

export default AdminPanel;
